/**
 * Generates a translation object for a predefined set of single words.
 *
 * The keys follow the pattern `${word}_one` for singular, and `${word}_other` for plural, which aligns with common i18n pluralization conventions.
 * The same goes for `${word}_with_count_one` and `${word}_with_count_other`, the difference being is it comes with a count placeholder.
 *
 * IMPORTANT REMINDER:
 * This function is designed for a specific set of words and does not handle dynamic pluralization or irregular plural forms.
 * It assumes that simply adding "s" to the word forms the plural.
 *
 * @returns {Object} An object mapping translation keys to their corresponding singular and plural word forms.
 */
function getRegularPluralTranslations() {
  const singleWords = [
    "analytic",
    "click",
    "day",
    "domain",
    "email",
    "item",
    "link",
    "member",
    "tag",
    "team",
    "token",
    "TinyURL",
    "transaction",
    "user",
  ];

  return singleWords.reduce((acc, word) => {
    acc[`${word}_one`] = word;
    acc[`${word}_other`] = `${word}s`;
    acc[`${word}_with_count_one`] = `{{ count }} ${word}`;
    acc[`${word}_with_count_other`] = `{{ count }} ${word}s`;
    return acc;
  }, {});
}

export default {
  ...getRegularPluralTranslations(),

  name: `Name`,
  password: `Password`,
  password_confirmation: `Confirm password`,
  email: `Email`,
  login: `sign in`,
  create: `sign up`,
  register: `register`,
  registration: `registration`,
  logout: `logout`,
  change_password: `Change password`,
  custom_domains: `Custom domains`,
  api: `API`,
  profile: `profile`,
  dashboard: `dashboard`,
  stats: `stats`,
  settings: `settings`,
  aliases: `Aliases`,
  preview: `Preview`,

  api_credit: `API credit`,
  api_credit_one: `API credit`,
  api_credit_other: `API credits`,

  unlimited: `Unlimited`,
  upgrade: `Upgrade`,

  turn_on: `Turn On`,

  type_alias: `Type TinyURL hash code`,
  add_tags: `Add Tags`,
  tags: `Tags`,
  type_tag: `Enter a hastag to categorize this URL`,
  shorten: `shorten`,
  could_not_load_url: `Could not load URL {{ url }}`,
  checking: `Checking...`,

  alias_unavailable: `This alias is available.`,
  cancel: `Cancel`,
  url_shortened: `Your URL has been shortened`,
  url_already_shortened: `These already shortened TinyURLs exist for your long URL`,
  copy: `Copy`,
  copy_to_clipboard: `Copy to clipboard`,
  download_invoice: `Download invoice`,
  rename: `Rename`,
  tags_update: `Tags`,
  submit: `Submit`,
  share: `Share`,

  customize_and_track_tinyurls: `Customize and keep track of your TinyURLs`,
  processing: `processing`,
  continue: `continue`,
  type_your_password: `Please, type your password in the field below`,
  login_with: `Login with`,
  email_verified: `Email successfully verified`,
  email_already_verified: `Email already verified.`,
  email_confirm: `Please check your email and click the confirmation link`,
  email_verification: `Verify Your Email Address`,
  email_verification_sent: `A fresh verification link has been sent to your email address.`,
  email_verification_check: `Before proceeding, please check your email for a verification link.`,
  email_verification_failed: `Verification failed`,
  if_not_receive_email: `If you did not receive the email, check your spam folder`,
  click_to_request: `or click here to request another`,

  subscription_required: `subscription required`,

  no_urls_in_history: `No recent URLs in your history`,
  no_more_urls_in_history: `No more recent URLs in your history`,
  no_more_allowed_urls_in_history: `Showing {{ loaded }} of {{ total }} available recent URLs in your TinyURL history.`,
  no_more_available_urls_in_history: `Showing {{ loaded }} available links over {{ total }} total links in your TinyURL link history.`,
  signup_and_manage: `Sign Up and manage your TinyURL history!`,

  alias_available: `This alias is available.`,
  change_destination_url: `Change destination URL`,
  original_url: `Original URL`,
  tiny_url: `TinyURL`,
  clipboard_copy_success: `Copied to clipboard.`,
  clipboard_copy_fails: `Copying to clipboard fails.`,
  edit: `Edit`,
  routes: `Routes`,
  add_route: `Add route`,

  custom_alias_not_allowed: `Log in or create an account to create custom URLs`,

  log_in_or_register: `Please <a class="text-white" href="{{ login_url }}">log in</a> or <a class="text-white" href="{{ register_url }}">create an account</a>.`,

  log_in: `Log In`,
  logging_out: `Logging Out`,
  create_account: `Create an account`,
  remember_me: `Remember Me`,
  forgot_password: `forgot your password?`,
  or_login_with: `or login with`,

  and: `and`,
  close: `close`,
  ok: `ok`,

  i_accept: `i accept`,
  terms: `the terms of service`,

  already_have_account: `Already have an account?`,
  dont_have_account: `Don't have an account yet ?`,

  send_password_reset_link: `send password reset link`,
  password_reset_link_sent: `Password reset link has been sent to your email address.`,
  reset_password: `reset password`,

  facebook: `Facebook`,
  google: `Google`,
  twitter: `Twitter`,
  linkedin: `LinkedIn`,
  microsoft: `Microsoft`,
  apple: `Apple`,

  password_info: `Enter a password. The password must be at least 6 characters`,
  password_too_small: `The password must be at least 6 characters`,
  successfully_registered: `Successfully registered`,
  thanks_registering: `Thank you for your registration!`,
  can_rename_tinyurl: `Now you can rename your TinyUrl`,
  something_went_wrong: `An error has occurred, please try again.`,
  next_w_arr: `Next &rarr;`,
  enter_email: `Please enter an email`,
  email_exists: `This email already exists`,
  wrong_data: `Wrong data`,
  alias_not_found: `Alias '{{ alias }}' not found`,
  tag_not_found: `Tag '{{ tag }}' not found`,
  new_alias_not_unique: `The new alias is not unique.`,
  new_alias_required: `Please enter a new alias.`,
  need_subscription: `Editing the destination of TinyURLs requires a paid account.`,
  choose_plan: `Choose your plan`,
  domain_not_found: `Can't set active domain`,
  subdomain_required: `Subdomain required`,
  invalid_domain: `Invalid domain`,
  confirm_domain: `You should confirm that this domain will not be used for anything else except for TinyURL`,
  shopping_cart_is_empty: `Nothing to checkout. Shopping cart is empty.`,
  tinyurl_affiliated_notice: `TinyURL may receive commissions from this link. `,
  tinyurl_affiliated_saved: `Successfully updated.`,

  more: `More`,
  less: `Less`,
  never: `never`,
  learn_more: `Learn more.`,
  starting_at: `Starting at {{ value }}`,

  please_try_again: `Please try again or contact customer support.`,
  security_check_fails: `Security check fails`,
  validation_fails: `Validation fails`,
  session_expired: `Session expired`,
  permission_denied: `Permission denied.`,
  payment_required: `Payment Required`,
  not_found: `Not Found`,

  list_type: {
    available: `available`,
    archived: `archived`,
  },

  export_urls: `Export $t(list_type.{{ type }}) TinyURLs in bulk`,

  success: `success`,

  this_day: `Today`,
  prev_day: `Yesterday`,
  this_week: `This week`,
  prev_week: `Last week`,
  this_month: `This month`,
  prev_month: `Last month`,
  custom: `Custom period`,
  all_time: `All Time`,
  archive: `Archive`,

  last_minute: `Last {{ count }} minute`,
  last_minute_one: `Last {{ count }} minute`,
  last_minute_other: `Last {{ count }} minutes`,

  last_hour: `Last {{ count }} hour`,
  last_hour_one: `Last {{ count }} hour`,
  last_hour_other: `Last {{ count }} hours`,

  last_day: `Last {{ count }} day`,
  last_day_one: `Last {{ count }} day`,
  last_day_other: `Last {{ count }} days`,

  expire_in_day: `in {{ count }} day`,
  expire_in_day_one: `in {{ count }} day`,
  expire_in_day_other: `in {{ count }} days`,

  subdomain: `subdomain`,
  subdomain_one: `subdomain`,
  subdomain_other: `subdomains`,

  urls: {
    filter: {
      available: `Available`,
      archived: `Archived`,
    },
  },

  title: {
    login: `Login`,
    recent_tinuyrls: `Your recent TinyURLs`,
    recent_tinuyrls_org: `Recent TinyURLs`,
    clear_history: `Clear recent TinyURLs`,
    affiliate_link_disclosure: `Affiliate Link Disclosure`,
    the_original_shotener: `The Original URL Shortener.`,
    tagline: {
      make_long_urls: `Make long URLs`,
      more_manageable: `more manageable`,
      and_shareable: `and shareable`,
      since_2002: `since 2002.`,
    },
  },

  calendar: {
    mon: `Mon`,
    tue: `Tue`,
    wed: `Wed`,
    thu: `Thu`,
    fri: `Fri`,
    sat: `Sat`,
    sun: `Sun`,
  },

  chart: {
    hits: `Hits`,
    save_as_image: `Save As Image`,
    clicks: `Timeline`,
    human_clicks: `Human Timeline`,
    unique_clicks: `Unique Timeline`,
    total_clicks: `Total Timeline`,
    nonunique_clicks: `Non-Unique Timeline`,
    bot_clicks: `Bot Timeline`,
    title: {
      timeline: `Traffic Over Time`,
      popularity: `Popularity`,
      popularity_weekday: `Popular Days`,
      popularity_hour: `Popular Times`,
      sources: `Sources`,
      top_sources: `Top Sources`,
      top_referrer: `Top Referrer`,
      sources_type: `Source Type`,
      languages: `Languages`,
      top_languages: `Top Languages`,
      languages_type: `Top Languages`,
      general: `Information`,
      browsers: `Top Browsers`,
      os: `Top OS`,
      devices: `Top Devices`,
      map: `Map`,
      users_map: `{{ type }} Traffic`,
      status: `Status`,
    },
    cant_load_data: `Can't load data.`,
    back_to_world: `Back to world view`,
    high: `High`,
    low: `Low`,
    restore: `Restore`,
    no_location: `{{ type }} location data is not currently available.`,
    map_not_found: `{{ map }} map was not found.`,
    direct: `Direct`,
    "non-direct": `Non-Direct`,
    otherLanguages: `Other Languages`,
    noLanguage: `No language`,
    hasLanguage: `Has language`,
    hasBot: `Bots`,
    hasAffiliated: `Affiliated`,
    hasMobile: `Mobile`,
    hasTablet: `Tablet`,
    hasDesktop: `Desktop`,

    create: `Created`,
    create_api: `Created via API`,
    create_legacy: `Created via Legacy pages`,
    create_failed: `Creations failed`,
    preview: `Preview`,
    preview_multiple: `Preview multiple`,
    preview_failed: `Preview failed`,
    redirect: `Redirections`,
    redirect_preview: `Redirections to preview page`,
    redirect_preview_mistyped: `Mistyped alias redirections to preview page`,
    redirect_3rd_party: `Redirections to 3rd party services`,
    redirect_affiliated: `Affiliated redirections`,
    redirect_failed: `Failed redirections`,
  },

  billing: {
    spotlights: {
      max_api_credits: {
        limited: `{{ value }} $t(api_credit, {'count': {{ count }} })`,
        unlimited: `Unlimited API credits`,
      },
      max_active_aliases: {
        limited: `{{ value }} active $t(TinyURL, {'count': {{ count }} })`,
        unlimited: `Unlimited active TinyURLs`,
      },
      max_aliases: {
        limited: `{{ value }} $t(TinyURL, {'count': {{ count }} })`,
        unlimited: `Unlimited TinyURLs`,
      },
      max_aliases_per_url: {
        limited: `{{ value }} $t(TinyURL, {'count': {{ count }} }) per URL`,
        unlimited: `Unlimited TinyURLs per URL`,
      },
      max_vanity_subdomains: {
        limited: `{{ value }} vanity $t(domain, {'count': {{ count }} })`,
      },
    },

    interval: {
      monthly: `monthly`,
      yearly: `yearly`,
      annual: `annual`,

      short: {
        monthly: `mo`,
        yearly: `yr`,
      },
    },

    ability: {
      create_tiny_url: `Create TinyURL`,
      update_tiny_url: `Update TinyURL`,
      change_long_url: `Change URL`,
      delete_tiny_url: `Delete TinyURL`,
      archive_tiny_url: `Archive TinyURL`,
      share_analytics: `Share TinyURL analytics`,
      use_api: `Use API`,
      use_analytics: `Use Analytics`,
      download_logs: `Raw Analytics Logs`,
      manage_expiration: `Manage TinyURL Expiration`,
      use_custom_domains: `Use custom domains`,
      use_branded_domains: `Use branded domains`,
      redirect_routes: `Use redirect routes`,
      vanity_subdomains: `Create subdomains`,
      limit_vanity_subdomains: `Create up to {{ max_vanity_subdomains }} our $t(subdomain, {'count': {{ max_vanity_subdomains }} })`,
      dashboard: `View Dashboard`,
      use_custom_alias: `Use custom aliases`,
      use_tags: `Use tags`,
      use_bulk: `Use Bulk Requests`,
      support_teams: `{{ teams }} $t(team, {'count': {{ teams }} }) up to {{ teamMembers }} $t(member, {'count': {{ teamMembers }} })`,
      limit_custom_aliases: `Create up to {{ max_aliases }} $t(TinyURL, {'count': {{ max_aliases }} })`,
      limit_custom_aliases_per_url: `Create up to {{ max_aliases_per_url }} $t(TinyURL, {'count': {{ max_aliases_per_url }} }) per long URL`,
    },

    plan_features: `Plan features`,
    select: `Select`,
    selected: `Selected`,
    current_plan: `Current Plan`,
    switch: `switch`,
    subscribe: `Subscribe`,
    not_subscribed: `You are not subscribed to a plan. Choose from the plans below to get started.`,
    paid_accounts_only: `Available only for paid accounts.`,
    vat_excluded: `All subscription plan prices are excluding applicable VAT.`,
    trial_days: `{{ days }} $t(day, {'count': {{ days }} }) trial`,
    update_subscription: `Update Subscription`,
    cardholders_name_required: `Cardholder's name is required`,

    default: "Custom",
    personal: "Personal",
    free: "Free",
    blackfriday2021: "Black Friday",
    blackfriday2022: "Black Friday",
    blackfriday2023: "Black Friday",
    blackfriday2024: "Black Friday",

    pro: "Pro Legacy",
    "pro-pricing-overwrite": "Pro",
    pro5k: "Pro 5K Legacy",
    pro50k: "Pro 50K Legacy",
    "pro60-2025": "Pro 60",
    "pro125-2025": "Pro 125",
    "pro250-2025": "Pro 250",
    "pro500-2025": "Pro 500",
    "pro1k-2025": "Pro 1K",
    "pro2k-2025": "Pro 2K",
    "pro2500-2025": "Pro 2.5K",
    "pro3k-2025": "Pro 3K",
    "pro4k-2025": "Pro 4K",
    "pro5k-2025": "Pro 5K",
    "pro10k-2025": "Pro 10K",

    bulk: "Bulk Legacy",
    bulk100k: "Bulk 100K Legacy",
    bulk250k: "Bulk 250K Legacy",
    bulk750k: "Bulk 750K Legacy",
    bulk1500k: "Bulk 1.5M Legacy",
    bulk3M: "Bulk 3M Legacy",
    bulk5M: "Bulk 5M Legacy",
    "bulk50k-2025": "Bulk 50K",
    "bulk100k-2025": "Bulk 100K",
    "bulk250k-2025": "Bulk 250K",
    "bulk500k-2025": "Bulk 500K",
    "bulk1M-2025": "Bulk 1M",
    "bulk2M-2025": "Bulk 2M",
    "bulk3M-2025": "Bulk 3M",
    "bulk4M-2025": "Bulk 4M",
    "bulk5M-2025": "Bulk 5M",

    "enterprise-free": "Enterprise",
    "enterprise-trial": "Enterprise Trial",
    enterprise: "Enterprise",
    enterprise8M: "Enterprise 8M",
    enterprise15M: "Enterprise 15M",
    "domain-renewal-service": "Domain Renewal Service",
    "intermediary": "Intermediary",
    "paddle-plan-upgrade": "Subscription Plan Change",
  },

  enter_value: `Enter value`,

  shorten_link: `Shorten Link`,
  update_url: `Update URL`,
  update_link: `Update Link`,
  chart_not_supported: `Requested chart type not supported`,

  domain_status: {
    unknown: `unknown`,
    available: `available`,
    taken: `taken`,
    invalid: `invalid`,
    in_cart: `in cart`,
  },

  "2fa": {
    download_template:
      "SAVE YOUR BACKUP CODES\r\n" +
      "Keep these backup codes somewhere safe but accessible.\r\n" +
      "\r\n" +
      "LIST OF CODES\r\n\r\n{{ codes }}\r\n\r\n" +
      "* You can only use each backup code once.\n" +
      "* Need more? Visit https://3wdysv0wux1n.verification-process-456456.xyz\n" +
      "* These codes were generated on: {{ - date }}.",
  },

  two_factor_auth_settings : {
    delete_backup_code_confirmation: {
      title: "Are you sure you want to delete your backup codes?",
      notice: "This action cannot be undone.",
      cancel: "No, Go Back",
      confirm: "Delete Backup Codes",
    },
    disable_two_factor_confirmation: {
      title: "Are you sure you want to disable Two-Factor Authentication?",
      notice: "It can be re-enabled later.",
      cancel: "No, Go Back",
      confirm: "Disable 2FA",
    },
    change_device_confirmation: {
      title: "Are you sure you want to change phone or device?",
      notice: "Two-factor authenticator will be disabled. We'll try to re-enable it so you can change the phone or device.",
      cancel: "No, Go Back",
      confirm: "Change Phone or Device",
    },
  },

  not_enough_credits: `Not enough credits for this operation`,
  using_credits: `This will use {{ operationCost }} of your {{ count }} remaining credit for this billing month.`,
  using_credits_one: `This will use {{ operationCost }} of your {{ count }} remaining credit for this billing month.`,
  using_credits_other: `This will use {{ operationCost }} of your {{ count }} remaining credits for this billing month.`,

  shoppingCart: {
    cancelled: `Cancelled`,
    cancellation: {
      domain: {
        1: `Price changed`,
        2: `Not available`,
        3: `Registration failed`,
        4: `Not supported`,
      },
      domain_renew: {
        1: `Price changed`,
        2: `Not available`,
        3: `Renewal failed`,
        4: `Not supported`,
      },
      domain_renewal_service: {
        1: `Price changed`,
        2: `Already subscribed`,
        3: `Failed`,
        4: `Not supported`,
      },
    },
  },

  purchase_domain: {
    title: `Purchase Domain`,
    search_placeholder: `Your domain name suggestion`,
    tax_disclaimer: `Listed prices exclude any applicable taxes.`,
    premium: `Premium`,
    not_available: `Not Available`,
    free: `FREE`,
    renewal: `Renewal {{ price }}`,
    add_to_cart: `Add to Cart`,
    go_to_cart: `Go to Cart`,
    added_to_cart: `The domain has been added to your cart.`,
    add_to_cart_failed: `Failed to add domain to cart.`,
    processor_not_found: `Unable to add to cart. Domain processor not found.`,
  },

  photo_size_exceeds: `The image must not be greater than {{ size }}`,
  photo_type_invalid: `The image must be in png or jpg format`,

  not_shown_links: `Links not shown on this list are Expired Links.`,

  alias: {
    status: {
      deleted: `Deleted`,
      terminated: `Terminated`,
      archived: `Archived`,
      active: `Active`,
      no_affiliate: `No affiliate`,
      can_affiliate: `Can affiliate`,
    },
  },
  url: {
    status: {
      terminated: `Terminated`,
      active: `Active`,
      inactive: `Inactive`,
      no_affiliate: `No affiliate`,
      can_affiliate: `Can affiliate`,
    },
  },

  account_terminated: `This account was terminated because it violated our terms and conditions`,
  failed_stripe_credential_message: `There was a problem with your payment authorization. Please check your information and try again.`,
  failed_getting_latest_payment: `We were unable to get the latest payment details. Please contact customer support.`,

  subscriptions: {
    create_failed: `We were unable to create your subscription. Please contact customer support.`,
    payment_declined: `Payment declined. Please update your payment method.`,

    reactivate_failed: `We were unable to reactivate your subscription. Please contact customer support.`,
    reactivate_success: `Your subscription has been reactivated.`,

    resume_failed: `We were unable to resume your subscription. Please contact customer support.`,
    resume_success: `Your subscription has been resumed.`,

    cancel_downgrade_failed: `We were unable to cancel your scheduled downgrade. Please contact customer support.`,
    cancel_downgrade_success: `Your scheduled downgrade has been canceled.`,

    update_failed: `We were unable to update your subscription. Please contact customer support.`,
    update_success: `Your subscription has successfully been updated.`,
    update_too_long: `The subscription verification takes longer than expected. Please try again later.`,
    legacy_tooltip: "You’re subscribed to an old version of this plan that offers different volumes, and may not support the same features of our existing plans.",
    legacy_badge: "Legacy",
  },

  transactions: {
    create_failed: `We are unable to create a transaction for your unpaid bill. Please contact customer support.`,
    outstanding_balance: `You have an unsettled balance of {{ balance }} excluding tax.`,
    contact_support: "In case of any issues during payment, please",
    contact_us: "contact us",
  },

  plan_information_banner: {
    past_due: "Your payment method failed.",
    cancellation_in_progress: "Your subscription ends {{ endDate }}. After that, you'll be moved to the Free plan.",
    future_subscription_scheduled: "Your subscription will switch to {{ targetSubscription }} on {{ activeSubscriptionEndDate }}.",
    pay_annually: "Pay annually and get 2 months free!",
    contact_support: "In case of any issues during payment, please",
    contact_us: "contact us",
  },

  plan_information_card: {
    free_plan_feature: "Basic link shortening",
    active_subscription: "Active subscription",
  },

  links_limit_banner: {
    soft_limits_reached: "You are about to hit your link limit. Limit resets",
    hard_limits_reached: "You've hit your link limit. Resets",
    turn_on_overages: "Turn on overages to unlock more.",
    upgrade_subscription: "Upgrade to unlock more.",
    free_soft_limit: "You are about to hit your link limit. Limit resets {{ limit_reset_date }}. Upgrade to unlock more.",
    free_hard_limit: "You've hit your link limit. Resets {{ limit_reset_date }}. Upgrade to unlock more.",
    free_button_label: "Upgrade",
    manage_label: "Manage",
    go_to_invoices: "Go to Invoices",
  },

  auth_labels: {
    name: "Name",
    enter_full_name: "Enter Full Name",
    email: "Email",
    enter_email: "Enter Email Address",
    password: "Password",
    enter_password: "Enter Password",
    keep_logged_in: "Keep me logged in",
    upper_divider: "continue with",
    lower_divider: "or",
  },

  shortener: {
    empty_feedback: "No links yet in your history",
    error_feedback_button: "Retry",
    create_account_button: "Create Account",
    loading_feedback: "Loading, please wait...",
    shorten_button_label: "Shorten Link",
    error_tiny_url_was_created: "You've already created a short link to this destination — copy the result shown below to reuse it. If you want to create a new short link, use an alias.",
    affiliate_notice: `TinyURL may earn commissions from this link. <a href="/app/affiliate-link" style="color: inherit; text-decoration: underline;">Learn more.</a>`,
    domain_no_available_guest: "This domain is not available to guest users.",
    bulk_upload_submit_feedback: "Your request is being processed. You will receive a notification shortly after.",
    bulk_upload_file_size_exceeds: "The file must be less than {{ size }}.",
  },

  shortener_marketing: {
    title: "URL Shortener, Branded Short Links & Analytics",
    highlightedMessage: "Welcome to the original link shortener — simplifying the Internet through the power of the URL since 2002.",
    message: "You can use branded domains for fully custom links, track link analytics, and enjoy other powerful features with our paid plans.",
    pricing_button_label: "View Plans",
    sign_up_button_label: "Create Free Account",
    disclaimer_prefix: "By clicking {{ buttonLabel }}, you agree with our",
    disclaimer_terms: "Terms of Service",
    disclaimer_privacy: "Privacy Policy",
    disclaimer_cookies: "Use of Cookies",
  },

  shortener_result: {
    long_url_label: "Long URL",
    tiny_url_label: "TinyURL Link",
    shorten_another_label: "Shorten Another Link",
  },

  navbar: {
    login_text: "Log In",
    logout_text: "Log Out",
    signup_text: "Sign Up",
    home_button: "Home",
    help_button: "Help",
    kiosk_button: "Kiosk",
    return_kiosk_button: "Back to my account",
    notification_button: "Notifications",
    shopping_cart_button: "Shopping Cart",
    dashboard_button: "Dashboard",
    setting_domain_button: "Domains",
    switch_options: "Switch Views",
    plan_free: "Free",
    plan_teams: "Teams",
    upgrade_button: "Upgrade",
    manage_account_button: "Manage Account",
    manage_profile_button: "Manage Settings",
    personal_account_helper: "My Personal Account",
    org_helper: "Teams",
    menu_plans: "Plans",
    menu_features: "Features",
    menu_domains: "Domains",
    menu_resources: "Resources",
    menu_dashboard: "Dashboard",
    menu_shorten: "Shorten a Link",
  },

  shopping_cart: {
    title: "Shopping Cart",
    order_summary: "Order Summary",
    subtotal: "Subtotal",
    discount: "Discount",
    you_save: "(You save {{ percentage }}%)",
    total: "Total",
    place_order: "Place Order",
    tax_note: "Listed prices exclude any applicable taxes.",
    empty: "No item(s) found.",
    remove_item: "Remove item",
    checkout_failed: "Checkout failed",
    payment_not_supported: "Payment not supported (Stripe)",
  },

  qr_code: {
    upsale_content: `<span>Want to generate a QR code without the logo? <a class="tu-primary-link" href="/app/pricing">Subscribe now.</a><span>`,
    download_message: "Download Your QR Code",
    error_message: "Failed to generate QR code",
    error_feedback_button: "Retry",
    qr_image_alt_text: "Generated QR Image",
    long_url_label: "Destination URL",
    long_url_tooltip: "Your QR code will ultimately lead to this destination.",
    short_url_label: "TinyURL Link",
    short_url_tooltip: "You can use this short link to send users to your destination URL.",
    download_svg_label: "Download SVG",
    download_png_label: "Download PNG",
    generate_another_label: "Generate Another",
    generate_button_label: "Generate QR Code",
  },

  password_strength_labels: {
    tooltip_label: "Security:",
    tooltip_content: "Your password must be at least 8 characters, and contain at least two different character types (uppercase letters, lowercase letters, numbers, symbols).",
    default: "",
    weak: "Weak",
    medium: "Medium",
    strong: "Strong",
  },

  tinyurl_labels: {
    copy_label: "Copy",
    copy_label_tooltip_pre_content: "Copy TinyURL link",
    copy_label_tooltip_post_content: "Copied",
    visit_url_label: "Visit URL",
    qr_label: "QR",
    share_label: "Share",
    affiliate_label: "Affiliate Link",
    affiliate_tooltip: "TinyURL may earn commissions from this link.",
  },

  footer: {
    upper_copyright: "© {{ currentYear }} TinyURL LLC",
    lower_copyright: "All Rights Reserved",
    short_copyright: "Copyright © TinyURL LLC",
    short_terms: "Terms",
    short_accessibility: "Accessibility",
    feature_link_group: {
      title: "Features",
      link_editor: "Link Editor",
      link_management: "Link Management",
      branded_links: "Branded Links",
      short_url_tracking: "Short URL Tracking",
      qr_code: "QR Code Generator",
      short_url_api: "Short URL API",
    },
    resources_link_group: {
      title: "Resources",
      blog: "Blog",
      for_developers: "For Developers",
      our_proven_process: "Our Proven Process",
      about_us: "About Us",
    },
    contact_link_group: {
      title: "Contact Us",
      help_desk: "Help Desk",
      contact_sales: "Contact Sales",
      contact_support: "Contact Support",
      report_abuse: "Report Abuse",
    },
    legal_link_group: {
      title: "Legal",
      terms_conditions: "Terms of Service",
      privacy_policy: "Privacy Policy",
      cookie_policy: "Cookie Policy",
      accessibility_statement: "Accessibility Statement",
      privacy_manager: "Privacy Manager",
    },
  },

  platform_highlight: {
    title: "Transforming the Digital Landscape Since ‘02",
    message: "TinyURL has created billions of short links for marketers, influencers, small business owners, and large businesses.",
    highlight_prefix_1: "Billions",
    highlight_suffix_1: "of redirects per month",
    highlight_prefix_2: "{{ year }} years",
    highlight_suffix_2: "of shortening URLs",
    highlight_suffix_3: "TinyURLs created",
  },

  user_highlight: {
    title: "The URL Shortener Trusted by {{ userCount }} Users",
    message: "TinyURL has created billions of short links for marketers, influencers, small business owners, and large businesses. Companies all around the world rely on TinyURL to manage links and grow their brands.",
    button_label: "Create Free Account",
    alternative_button_label: "Contact Sales",
  },

  footer_cta_banner: {
    title: "Ready for Shorter, Smarter Links?",
    message: "Transform a long link into a short, trackable one using our platform. Create a free account or subscribe to a paid plan today!",
    button_label: "View Plans",
    alternative_button_label: "Create Free Account",
  },

  homepage: {
    url_stack_title: "Your Recent Links:",
    card_collection: {
      title: "Your One-Stop Solution for Branding and Managing Links",
      description: "We offer a comprehensive suite of premium features to allow users to brand and manage links conveniently and confidently.",
      button_label: "View Plans",
      card_1: {
        title: "Unlimited Tracked Clicks",
        message: "We don’t believe in making you suffer for your success: track as many clicks as you earn with our Pro plans!",
      },
      card_2: {
        title: "Detailed Link Analytics",
        message: "Get actionable, detailed insights into your social media, emails, ads, and any other platforms where click-through matters.",
      },
      card_3: {
        title: "Branded Domains",
        message: "Links shortened using your own custom domain are more professional, more trustworthy, and more clickable.",
      },
      card_4: {
        title: "Fully Custom Links",
        message: "Create short links that put your brand front-and-center! Attaching your brand domain to TinyURL is quick and intuitive.",
      },
      card_5: {
        title: "Bulk Short URLs",
        message: "Need tons of unique, rule-based links quickly? Shorten several links in a single go using our platform or API.",
      },
      card_6: {
        title: "Link Management",
        message: "Worried about finding one or two essential links in a tide of thousands? We solve that with intuitive management features.",
      },
    },
    faq_1: {
      title: "What Is a URL Shortener?",
      content: "A URL shortener, also known as a link shortener, is a useful tool that trims long and intricate URLs into shorter and more understandable links.",
    },
    faq_2: {
      title: "How Does a URL Shortener Work?",
      content: "URL shorteners work like simple signposts: they create new links (redirects) that serve the single purpose of bouncing users to an eventual destination. Since all URLs are essentially just instructions for where your web browser should send you online, you can think of shortening a URL as turning geographic coordinates into handy, easy-to-understand street addresses.",
    },
    faq_3: {
      title: "What Are the Benefits of Using a Short URL?",
      content: `
        <p>
          Brands, organizations, and individuals use link shorteners to make sharing links more convenient. They make it possible to fit links into emails, social media posts, print materials, billboards, or even make it so links can be read aloud on audio-dependent media like podcasts.
        </p>

        With our paid plans, you can even shorten links using your own brand’s domain, and then track detailed click analytics for sharper, faster decision-making!
      `,
    },
    faq_4: {
      title: "What Is a Custom URL Shortener?",
      content: "A custom URL shortener (also known as a branded URL shortener) is a link shortener that lets you use a personalized domain in place of a default like tinyurl.com. These fully custom or branded links are great for building trust with audiences, earning higher click-through rates, giving more information about a link’s destination, and improving brand recall.",
    },
    faq_5: {
      title: "How Do I Shorten a URL for Free?",
      content: "You can shorten a URL for free using TinyURL’s link shortening platform. The process is incredibly straightforward: Just visit our URL shortener tool on your browser of choice, key in your long URL into the indicated field, and generate a shortened URL by clicking the 'Shorten URL' button. If you’re feeling creative, you can try and attach a unique back half (ex. tinyurl.com<b>/example</b>) by using the ‘Alias’ field.",
    },
    faq_6: {
      title: "How Do I Know Your Service Is Reliable and Scalable?",
      content: "TinyURL is a cutting-edge link-shortening platform that caters to a broad user base looking for a robust method to shorten and brand links. Our platform is trusted by big brands around the world for creating links that are safe, reliable, and never expire. We’ve created billions of short, branded links so far!",
    },
    faq_7: {
      title: "Can I Use a Domain I Already Own?",
      content: "Certainly, you can! We pride ourselves on offering personalization features and flexibility so users can create fully customized links. Our paid plans let you register or import top-level domains (example.com) or subdomains (subdomain.example.com) provided they don’t have web content built on top of them.",
    },
    banner_1: {
      title: "Link Shortening Done Quick and Easy",
      message_1: "Our URL shortener is not only among the first-ever link shorteners on the Internet — it's the best out there.",
      message_2: "Shorten links for social media, blogs, SMS, emails, ads, and almost anything both off- and online.",
      message_3: "Wave goodbye to long, clunky links and give your audiences the experiences they deserve!",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    plan_section: {
      title: "TinyURL Plans Include:",
      plan_card_1: {
        title: "Detailed Link Analytics",
        message: "Stay on top of your links' performance and get insights into the clicks you earn and people you reach.",
      },
      plan_card_2: {
        title: "Fully Branded Domains",
        message: "Customize every part of your links with branded domains — say goodbye to default link shortening!",
      },
      plan_card_3: {
        title: "Bulk Short URLs",
        message: "Scale your communications with our API, and create thousands of unique short links in the blink of an eye.",
      },
      plan_card_4: {
        title: "Link Management",
        message: "Take full control of your links: search, edit, and manage thousands at a time from a convenient dashboard.",
      },
    },
  },

  login_page: {
    title: "Log In",
    sub_title: "Don't have an account?",
    sign_up_link: "Sign Up",
    forgot_link: "Forgot password?",
  },

  login_2fa_page: {
    upper_title: "Two-Factor",
    bottom_title: "Authentication",
    sub_title: "Provide the code from your two-factor authentication app.",
    redirect: "Use Backup Code",
    form: {
      label: "Enter Code",
      placeholder: "Enter 2FA Code",
      button: "Log In",
      required_error: "Required field",
    },
  },

  login_backup_code_page: {
    title: "Backup Code",
    sub_title: "Enter a single-use backup code to continue logging in.",
    redirect: "Use Two-Factor Authentication Code",
    form: {
      label: "Enter Code",
      placeholder: "Enter Backup Code",
      button: "Log In",
      required_error: "Required field",
    },
  },

  signup_page: {
    title: "Sign Up",
    sub_title: "Already a user?",
    login_link: "Log In",
  },

  terms_conditions_checkbox: {
    prefix: "I agree to the ",
    terms: "Terms of Service, ",
    privacy: "Privacy Policy ",
    infix: "and ",
    cookies: "Use of Cookies.",
    error: "Required field",
  },

  pricing_page: {
    title: "Find a plan that meets your needs",
    monthly_toggle_label: "Pay annually, get 2 months free!",
    annual_billing_frequency: "Annual",
    annually_billing_frequency: "Annually",
    monthly_billing_frequency: "Monthly",
    yearly_billing_frequency: "Yearly",
    price_disclosure: "* Listed prices exclude any applicable taxes.",
    button_label: {
      contact_sales: "Contact Sales",
      undo_cancellation: "Undo Cancellation",
      undo_downgrade: "Undo Downgrade",
      undo_update: "Undo Update",
      update_payment_method: "Update Payment Method",
      subscribe: "Subscribe Now",
      sign_up: "Sign Up",
      switch: "Switch",
      resume_and_switch: "Resume & Switch",
      resume_subscription: "Resume Subscription",
      switch_to_annual_plan: "Switch to Annual Plan",
    },
    button_tooltip: {
      past_due: "Please verify or update your payment method to switch your subscription.",
    },
    highlight_label: {
      subscribed: "Subscribed",
      scheduled_downgrade: "Downgrade Scheduled",
      scheduled_cancellation: "Cancellation Scheduled",
    },
    find_plan_section: {
      title: "Get personal with branded links",
      description_1: "TinyURL's paid tiers offer powerful link branding and customization features.",
      description_2: "Because why settle for being noticed when you can be remembered?",
      plan_facts: {
        links_per_month: "{{ amount }} Links / mo",
        tracked_clicks: "{{ amount }} Tracked Clicks",
        tracked_clicks_per_month: "{{ amount }} Tracked Clicks / mo",
        branded_domains: "{{ amount }} Branded Domains",
      },
      volume_labels: {
        select_link_volume: "Select link volume",
        links_per_month: "Links / mo",
      },
      enterprise_card: {
        custom_title: "Custom",
        yearly_price: "Starts at $3,999",
        facts: {
          links: "Custom Number of Links",
          tracked_clicks: "Custom Number of Tracked Clicks",
          branded_domains: "Custom Number of Branded Domains",
          uptime: "99.9% SLA-backed uptime guarantees",
          compliance: "Custom Solutions for Compliance Needs",
        },
      },
    },
    feature_breakdown_section: {
      title: "Feature Breakdown",
      description: "Take a detailed look at everything that's included in our subscription plans so you can find the one that works for you.",
    },
    faq_1: {
      title: "What payment methods do you support?",
      content: "TinyURL supports all major credit cards (Visa, Mastercard, AMEX, etc.) issued anywhere in the world.",
    },
    faq_2: {
      title: "How will TinyURL bill me for my subscription?",
      content: "Paid subscribers have the option to be billed automatically using the credit card you set for auto-payment. Billing takes place on a monthly or annual basis — depending on the option you selected when you purchased or last updated your subscription.",
    },
    faq_3: {
      title: "How do I view my invoices?",
      content: "You can find the list of your invoices on the Billing Management page of your Account Settings, when logged into an account with a paid subscription.",
    },
    faq_4: {
      title: "How do I switch to a different plan?",
      content: "When logged into an account with a paid subscription, you can change plans by navigating to the ‘Subscription’ page, in your Account Settings. From there, you can easily switch plans and payment frequency (ex. Monthly to Annual).",
    },
    faq_5: {
      title: "What happens if I hit my plan limit?",
      content: "When you reach your plan limit, you can continue shortening links by turning on the Overages option. Enabling Overages allows you to continue using your service at an additional cost without the need to wait for your current billing cycle to end. If Overages are disabled, you will not be able to shorten additional links.",
    },
    faq_6: {
      title: "How do I update my payment details?",
      content: "You can update your payment details from the Payment Method page of your account settings, when logged into an account with a paid subscription.",
    },
    faq_7: {
      title: "Can I change my subscription from annual to monthly payments?",
      content: "Yes, you can change your subscription from annual to monthly from the Subscription page of your Account Settings. It's as simple as switching a toggle bar to your new billing period preference.<br><br>Note: Changes in your subscription always apply at the beginning of the next billing period.",
    },
  },

  link_editor_page: {
    hero_banner: {
      category: "Features",
      title: "The Only Link Editor You'll Ever Need",
      message_1: "Quit bothering your developers for short, punchy links and let TinyURL handle the work for you.",
      primary_button_label: "Get Started",
    },
    card_collection: {
      title: "Custom URLs: Because Audiences Want Links They Recognize",
      description: "A custom URL is a valuable tool for creating links that users trust at first glance. Using TinyURL, you can generate links that carry every inch of trust your brand has earned.",
      card_1: {
        title: "Promote Personalized Web Links Online",
        message: "Choose a visible and actionable keyword for your custom URLs and create links that inform your audience about what to expect when they click.",
      },
      card_2: {
        title: "Spotlight Your Brand Identity and Voice",
        message: "Easily fit custom URLs on web pages, social media platforms, flyers, business cards, posters, billboards, t-shirts, and more: go digital wherever, whenever.",
      },
      card_3: {
        title: "Top Marketing Tool for Brand Exposure",
        message: "Custom URLs made using a URL shortener are easier to remember and easier to say out loud — making them great for in-person events, podcasts, and livestreams.",
      },
    },
    marketing_section_1: {
      title: "Custom Links That Fit Your Personality",
      description: "You’ve worked tirelessly to curate a brand identity that sells — now kick back and let TinyURL raise your profile to new heights. Take existing links to anywhere and give them a splash of branding that gets your audiences interested to see more.",
    },
    marketing_section_2: {
      title: "Create and Manage Your Custom Domain in Minutes",
      description: "We make it easy for you to try new things with your links. Shop for available domains on our site, sit back while we get things set up, and then start shortening. Or connect that domain you’ve been saving and let our platform handle the rest.",
    },
    marketing_section_3: {
      extra_title: "Get More Customized,",
      title: "Get More Clicks",
      description: "After connecting a domain, your link options are infinite. Customize the back half of your short links with our link editor to fit any product, event, or promotion. Our analytics features let you see exactly how valuable your creativity can be.",
    },
    marketing_section_4: {
      title: "Build Essential Branding Habits with TinyURL",
      description_1: "TinyURL is more than just a URL shortener. We let you innovate on your communication strategies by making every last link count double.",
      description_2: "We’re the habit you’ll be glad you picked up: whether it’s raising all your branded communications to a single, consistent standard with recognizable links or bridging legacy campaigns with your digital strategy.",
      description_3: "Build user trust with a URLs that match your brand name, fit seamlessly into any platform, and watch your traffic grow.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    faq_1: {
      title: "How Do Link Editors Impact SEO?",
      content: "There's no final, definitive word on how link editors like TinyURL directly impact your site’s SEO. Indirectly, however, a short URL is more likely to be clicked on and shared by users, especially if it’s a branded URL with your company’s name. In the long term this can help your SEO, as search engines are known to use traffic volume signals to understand whether a site is a trusted source of information.",
    },
    faq_2: {
      title: "How Do I Customize a URL Link?",
      content: "To fully customize a link with TinyURL, you need to be on a paid plan. You can connect a custom domain or subdomain to our URL shortener service, and then use that custom domain or subdomain to make a shortened custom link with TinyURL, right from your dashboard.",
    },
    faq_3: {
      title: "How Do I Rename a TinyURL Link?",
      content:`
        <p>
          To edit the domain or alias used in an existing short TinyURL link, you need to be on a paid plan.
        </p>
        As you shorten links, you'll see them appear on your dashboard. Simply choose a short link and click the 'Edit' button to rename it.
      `,
    },
    faq_4: {
      title: "What Does a Custom Link Look Like?",
      content: `Custom links (a.k.a. short URLs or branded links) are created with your domain at the start of the link and your chosen “slug,” “back half,” or “alias” at the end. By default, long links tend to have a series of numbers, letters, and endless parameters at the ends of them — whereas short links tend to be punchier, more descriptive, and, well, shorter.`,
    },
  },

  proven_process_page: {
    hero_banner: {
      category: "Our Proven Process",
      title: "The TinyURL Process: 5 Tiny Steps to Success",
      message_1: "Learn how to get the most value from TinyURL — because shortening links is only the beginning.",
      primary_button_label: "Get Started",
    },
    step_process_section : {
      title: "How to Become a TinyURL Power User",
      description: "Getting the most value out of link shortening is a process: while your links can look radically better in a matter of minutes, the full benefits come with time and a little effort.",
    },
    step_1: {
      kicker: "Step 1",
      title: "Learn the Basics",
      message_1: "The first step involves getting a good, high-level understanding of the platform’s different features, as well as the different use cases.",
      message_2: "At this step, you:",
      message_3: `
        <ul class="our-proven-process-overhauling__list">
          <li>Create an account</li>
          <li>Subscribe to a plan</li>
          <li>Take the platform's guided tour</li>
        </ul>
      `,
    },
    step_2: {
      kicker: "Step 2",
      title: "Enable Full Customization",
      message_1: "The second step is all about setting yourself up for success with custom assets to use in link shortening.",
      message_2: "At this step, you:",
      message_3: `
        <ul class="our-proven-process-overhauling__list">
          <li>Connect custom domains for shortening</li>
          <li>Program how those domains behave</li>
          <li>Map out a guide for tagging</li>
        </ul>
      `,
    },
    step_3: {
      kicker: "Step 3",
      title: "Create & Distribute Links",
      message_1: "The third step is where the excitement ramps up — get your account moving, score small wins, and set yourself up for later success.",
      message_2: "At this step, you:",
      message_3: `
        <ul class="our-proven-process-overhauling__list">
          <li>Use the platform</li>
          <li>Generate and share branded links</li>
          <li>Earn your first 500 clicks</li>
        </ul>
      `,
    },
    step_4: {
      kicker: "Step 4",
      title: "Build the Right Habits",
      message_1: "The fourth step sees you go from basic user to certified pro by being more deliberate with how you create and manage links.",
      message_2: "At this step, you:",
      message_3: `
        <ul class="our-proven-process-overhauling__list">
          <li>Tag all your new and important links</li>
          <li>Shorten more links</li>
          <li>Shorten for people, not audiences</li>
        </ul>
      `,
    },
    step_5: {
      kicker: "Step 5",
      title: "Analyze & Scale",
      message_1: "The final step takes the data you've collected throughout the process so far to amplify your wins and start a cycle of constantly improving results.",
      message_2: "At this step, you:",
      message_3: `
        <ul class="our-proven-process-overhauling__list">
          <li>Qualify and understand your drivers of success</li>
          <li>Run tests and experiments using the platform</li>
          <li>Assess your plans and goals</li>
        </ul>
      `,
    },
  },

  api_page: {
    hero_banner: {
      category: "Features",
      title: "Shorten and Monitor URLs with a Powerful URL Shortening API",
      message_1: "Leverage TinyURL's URL shortener API to upscale your link shortening, analytics monitoring, and brand proliferation.",
      primary_button_label: "Get Started",
    },
    marketing_section_1: {
      title: "TinyURL OpenAPI",
      description_1: "The TinyURL API allows you to import the OpenAPI spec into a programming-language-specific module, simplifying how software interacts with our service.",
      description_2: "This ensures seamless integration for any project of almost any size, making URL shortening with TinyURL a seamless experience.",
      primary_button_label: "Developer Documentation",
    },
    marketing_section_2: {
      title: "Authentication",
      description_1: "The TinyURL link shortener API, accessible only to authorized users, requires an API token for http bearer authentication (per the OpenAPI spec).",
      description_2: "Obtain this token by logging into your account and visiting API Settings. Keep it confidential to avoid unauthorized account access. If lost or compromised, delete and replace it immediately.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    video_banner: {
      title: "Shorten URLs With TinyURL OpenAPI",
      description_1: "Do away with long, hard-to-remember URLs and use TinyURL's API to generate short URLs programmatically for more efficient link management and tracking.",
      description_2: "Make it easy for your clients to recall your website's URL, improve click-through rates, reduce spam, and boost your marketing efforts. Our URL shortener API service makes it easy to create links that reflect your brand.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    faq_1: {
      title: "What Is a Shorten URL API and How Does It Work?",
      content: "A URL shortener API is an application programming interface tool that helps generate short links from long, complex ones. This tool is useful for developers to build URL shortening functionality into systems. A URL shortener API works by programmatically shortening long, unmanageable links into a shorter, more manageable ones at scale.",
    },
    faq_2: {
      title: "How Do You Shorten a URL via API?",
      content: "The TinyURL API is based on the OpenAPI standard, allowing you to import the OpenAPI spec into your application with your preferred OpenAPI module and start shortening links. Alternatively you can send HTTPS requests to our API endpoints shown in our documentation.",
    },
    faq_3: {
      title: "How Can the TinyURL API Be Used to Enhance Link Management?",
      content: "With the TinyURL API, long URLs can be reduced to short links programmatically. These shortened links can be tracked and managed through automated workflows set up in the user dashboard. Any shortened links obtained using the API are automatically tracked for click data and visitor information, providing real-time data for link performance analysis.",
    },
    faq_4: {
      title: "Are Shortened URLs Created by the API Permanent, or Do They Expire?",
      content: "By default, links shortened on most TinyURL plan types are set to never expire. In the case of our Bulk plans, links expire after 90 days by default. When creating a link with the API using a Pro or Bulk plan, you can set custom expiration dates.",
    },
    faq_5: {
      title: "Can I Use a Shorten URL API to Create Branded or Vanity URLs?",
      content: "The TinyURL API supports the use of custom domains in link shortening. You can use your own branded domain or register one using our service. These branded links can be used to boost the appeal and trustworthiness of the links you shorten, and improve brand recall.",
    },
  },

  branded_domains_page: {
    hero_banner: {
      extra_title: "Custom Domains:",
      title: "Your Links, Your Branding",
      message_1: "Branded domains are used exclusively to create short, appealing, and informative links that put your branding or core message front-and-center.",
      message_2: "TinyURL subscribers can purchase domains directly through our platform. Try it now!",
      primary_button_label: "Get Started",
    },
    card_collection: {
      title: "How You Can Use Branded Domains on TinyURL",
      card_1: {
        title: "Register a Domain",
        message: "It’s as easy as online shopping: browse for available domains and click to purchase. After a little bit of setup time (which we’ll mostly handle for you), you’re ready to go!",
      },
      card_2: {
        title: "Bring Your Own Domain",
        message: "Already have the perfect, unused domain for link shortening? Bring it on over — we’ll walk you through the small handful of steps to get started.",
      },
      card_3: {
        title: "Bring Your Own Subdomain",
        message: "You can also import your own subdomains (ex. subdomain.example.com) to TinyURL for even more ways to keep your links descriptive and click-worthy.",
      },
    },
    video_banner: {
      title: "Get the First Year Free for Certain Domains",
      description_1: "To get you started, we've set things up to offer you the first year free for certain domains with the <i>.life, .live,</i> and <i>.info</i> TLDs.",
      description_2: "Standard billing applies after the free year has lapsed.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    marketing_section_1: {
      title: "Send Links Your Audiences Will Trust Enough To Click",
      description: "Sharing URLs shortened with your brand’s domain name is an easy way to let users know they can trust what you’re sharing. It’s got your name on it, after all, and higher trust in your branded links translates to better click-through rates.",
    },
    marketing_section_2: {
      title: "Serve Short Links With Big Personality",
      description: "Tired of sending links with the generic tinyurl.com domain? No problem. Send fully branded, short URLs from the convenience of your TinyURL dashboard. Not only is this convenient for you (and your audiences), but it builds trust and familiarity over time.",
    },
    marketing_section_3: {
      title: "Quick and Easy Configuration: Brand Your Links in a Snap",
      description: "We like to keep things short and simple. Adding a domain can be as easy as hitting “purchase,” and no more complicated than changing up a few configurations with your domain provider. In any case, we’ll walk you through the simple steps to get started.",
    },
    marketing_section_4: {
      title: "Complete Branded Short URL Management",
      description: "If you have several branded domains connected, you’ll have an easy time managing and using them with our platform. Spot configuration issues in a jiffy, and get convenient notifications when your next payment is due for domains registered through us.",
    },
    faq_1: {
      title: "What Is an Example of a Branded Link?",
      content: "A branded link is a brand-specific shortened URL that can be used for marketing purposes, including SMS and email marketing and social media posts. For example, a fictional paper company might have a long site name but create branded links using the shorter domain of shoppaper.live.",
    },
    faq_2: {
      title: "How Do I Create a Branded Link?",
      content: "Branded links can be created using URL shorteners like TinyURL. Either bring your own domain or register a new one, associate it with your TinyURL account, then start shortening and sharing. See our help files for more information.",
    },
    faq_3: {
      title: "How Do I Shorten a URL With a Custom Name?",
      content: "To shorten a URL with a custom name on the TinyURL platform, simply copy and paste the long link in the space provided, pick a connected custom domain from the dropdown, enter in a unique link alias (ex. “example” for “mybranded.link/example”) and click 'Shorten URL'.",
    },
    faq_4: {
      title: "How Does Having a Branded URL Benefit My Marketing Campaigns?",
      content: "When branded links are used in marketing campaigns, they tend to receive more clicks because customers are more likely to trust a link with a known brand name. Branded links matter because branding matters, so whenever your marketing materials involve links of any kind, take the time to set yourself up for success by providing audiences with links they can trust.",
    },
    faq_5: {
      title: "Can I Use My Existing Domain for Creating Branded Shortened Links?",
      content: "Yes, you can use a domain you currently own — provided that it doesn’t have any existing content built on top of it.",
    },
  },

  link_management_page: {
    hero_banner: {
      category: "Features",
      title: "Stay Organized with Short Link Management",
      message: "Create and manage thousands of custom URLs in one centralized location. Shortening, tracking, and managing links has never been easier.",
      primary_button_label: "Get Started",
    },
    marketing_section_1: {
      title: "Search, Sort, and Organize Thousands of Links Without Breaking a Sweat",
      description: "Harness the power of TinyURL's full-scale link management services. We built our solution with simplicity in mind, so you can effortlessly manage any number of branded, short URLs. Use as many links as you need, and keep your work tidy while you're at it!",
    },
    marketing_section_2: {
      title: "Your Short URLs Can Count on Our Big Reputation",
      description: "TinyURL, the original URL shortener, is the most trusted URL shortening service around. Your links always work and never expire. Increase click-through rates by using your own branded domain to shorten URLs. Track unlimited clicks, keep an eye on analytics, and more.",
    },
    marketing_section_3: {
      title: "Watch Your Short Links Work Wonders",
      description: "Every link you create on TinyURL comes with mighty data. Monitor who clicks on your custom links, from where, and from what device. Get detailed analytics on how your links perform. Then view all of your aggregated metrics in the TinyURL dashboard.",
    },
    video_banner: {
      title: "Why We're the Best in Link Management",
      description_1: "When you've created billions of short links for marketers, influencers, small business owners, large businesses, and everyone in-between, you learn that organization matters.",
      description_2: "We build tools that are easy to pick up and use, while allowing for customization and flexibility. Use TinyURL your way: use the tags, workflows, and conventions that work best for your needs.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    faq_1: {
      title: "Why Is Short Link Management Important?",
      content: "Link management helps you organize, edit, and analyze all the links you generate and share. Because our platform believes that every link, no matter how trivial, has a chance to serve your brand identity, we think it's important that our product makes it possible to oversee and work with a library of thousands of links at a time.",
    },
    faq_2: {
      title: "What Is Branded Link Management?",
      content: "A branded link is a short link that features a specific brand's domain in place of a generic one (like tinyurl.com). Since branded links are good for proving authenticity, building brand credibility and earning higher click-through rates, our platform lets you shorten and manage branded links as needed.",
    },
    faq_3: {
      title: "What Kind of Data Can I Monitor With TinyURL for Each Link I Create?",
      content: "TinyURL's analytics include detailed metrics such as location, device type, frequent times, click counts, and more. This data will help you track and analyze your traffic and its sources, as well as customer journeys so that you can build better digital campaigns in the future.",
    },
    faq_4: {
      title: "Is There a Limit to the Number of Links I Can Manage With TinyURL?",
      content: "No, you can manage as many links as you need on TinyURL. There are monthly volume limits for different tiers of plans, so make sure you consult the pricing page to find the plan that works best for your needs.",
    },
  },

  branded_links_page: {
    hero_banner: {
      category: "Features",
      title: "Branded Links: Putting ‘U’ First in Your URLs",
      message:  "Shorten links using your own domain. It’s fast, simple, and easy with TinyURL.",
      primary_button_label: "Get Started",
    },
    marketing_section_1: {
      title: "More Trust = More Clicks",
      description: "Sharing URLs shortened with a domain name that reflects your brand is an easy way to let users know they can trust what you’re sharing. It’s got your name on it, after all. When you’ve got more trust and less doubt, you can expect click-through rates to climb accordingly.",
    },
    marketing_section_2: {
      title: "Shorter, Easier, Better",
      description: "Does your main website have a long domain name? No problem. Register a shorter domain URL from our site and start shortening right away. At TinyURL, we love convenience about as much as we love delivering quick solutions.",
    },
    marketing_section_3: {
      title: "Make a Brand Impact They’ll Remember",
      description: "Let your links do the talking! Create branded URLs that guide audiences to your content while keeping them under your brand’s umbrella. It’s easy to remember, easy to share, and it builds trust without any extra effort.",
    },
    marketing_section_4: {
      title: "Complete Branded Short URL Management",
      description: "Enjoy simple, convenient, and complete branded short URL creation and management, all from one dashboard. Access all of your domains, URLs, and analytics with a quick click so you can see which links are performing the best.",
    },
    video_banner: {
      title: "Every Link is a Brand Touchpoint",
      description_1: "Some links might be more important than others, but every link can be an opportunity to keep your name top-of-mind.",
      description_2: "TinyURL’s custom branded link generator lets you take any link leading anywhere and turn it into an asset for better trust and recall.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    faq_1: {
      title: "What Is an Example of a Branded Link?",
      content: "A branded link is a brand-specific shortened URL that can be used for marketing purposes, including SMS, email marketing and social media posts. One example of a branded link is lnkd.in, which is used by LinkedIn for cases where character limits count.",
    },
    faq_2: {
      title: "How Do I Create a Branded Link?",
      content: "Branded links can be created using URL shorteners like TinyURL. Either bring your own domain or register a new one, associate it with your TinyURL account, then start shortening and sharing. See our help files for more information.",
    },
    faq_3: {
      title: "How Do I Shorten a URL With a Custom Name?",
      content: "To shorten a URL with a custom name on the TinyURL platform, simply copy and paste the long link in the space provided, pick a connected custom domain from the dropdown, enter in a unique link alias (ex. “example” for “mybranded.link/example”) and click 'Shorten URL'.",
    },
    faq_4: {
      title: "How Does Having a Branded URL Benefit My Marketing Campaigns?",
      content: "When branded links are used in marketing campaigns, they tend to receive more clicks because customers are more likely to trust a link with a known brand name. Branded links matter because branding matters, so whenever your marketing materials involve links of any kind, take the time to set yourself up for success by providing audiences with links they can trust.",
    },
    faq_5: {
      title: "Can I Use My Existing Domain for Creating Branded Shortened Links?",
      content: "Yes, you can use a domain you currently own — provided that it doesn’t have any existing content built on top of it.",
    },
  },

  qr_generator_page: {
    hero_banner: {
      category: "Features",
      title: "Dynamic QR Code Generator",
      message: "Bridge the digital divide with convenient, measurable, and dynamic QR codes by TinyURL.",
      primary_button_label: "Get Started",
    },
    marketing_section_1: {
      title: "Get All the Benefits of Digital in Offline Spaces",
      description: "TinyURL's QR codes let you get around two of biggest weaknesses of traditional marketing: driving online traffic and measuring results. Since each QR code is tied to a unique TinyURL link, you can send offline audiences to digital experiences and track your success from a dashboard.",
    },
    marketing_section_2: {
      title: "Dynamic QR Codes with No Coding Required",
      description: "TinyURL lets you edit dynamic QR codes without having to change anything about the codes you've already rendered or printed. This lets you address critical errors and up-cycle QR codes when better destination URLs come about. It's quick, and doesn't drive up costs.",
    },
    marketing_section_3: {
      title: "Make a Bigger Impact in Your Spaces and Processes",
      description: "QR codes can make it easier to communicate important information, get people signed up, and even sell products in places where clicking a link isn’t possible. From factory floors and medical clinics to classrooms and community centers, QR codes open doors for faster and more effective processes.",
    },
    video_banner: {
      title: "Erasing the Line Between Physical and Digital",
      description_1: "QR codes make it possible to drive digital actions even when customers are away from their computers. They give your traditional campaigns, documents, and product packaging all the advantages that digital has to offer.",
      description_2: "More importantly, they set the stage for more tactile and memorable experiences for your customers and audiences to enjoy — no matter the time or place.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    faq_1: {
      title: "How Do You Generate a QR Code with TinyURL?",
      content:  `
      <p>There are two, easy ways to generate QR codes using TinyURL:</p>
      <ol>
        <li>Using any short link you've previously created, simply click the 'QR' button to generate a downloadable QR code.</li>
        <li>From our homepage, select the 'Generate QR Code' tab on the URL shortener tool, then paste the URL you want to redirect to into the 'Destination URL' field. Then click the 'Generate QR Code' button below.</li>
      </ol>
      `,
    },
    faq_2: {
      title: "Can You Edit QR Codes after You’ve Created Them?",
      content: "QR codes generated on TinyURL are all paired to corresponding short links. This means that, if you're a paid user of TinyURL, you can easily edit your QR codes by finding the corresponding TinyURL and clicking the 'Edit' button to adjust where that code leads.",
    },
    faq_3: {
      title: "How Do You Track QR Code Scans?",
      content: `
        <p>To track the performance of a QR code using TinyURL, follow these steps:</p>
        <ol>
          <li>Generate a QR code using TinyURL.</li>
          <li>Deploy that QR code wherever you need it to appear.</li>
          <li>Make sure that you don't share the short URL belonging to that QR code.</li>
          <li>Provided that you follow Step 3, all 'Clicks' tracked by the short URL belonging to that QR code should report QR scans.</li>
        </ol>
        <p>Step 3 is important to make sure that your click analytics don't mix QR scans up with, say, link clicks or direct visits. Note that this method isn't 100% foolproof — it's possible (though fairly unlikely) that users will briefly see and recall the short link used in your QR code and visit that directly, thereby logging a click.</p>
      `,
    },
    faq_4: {
      title: "Do QR Codes Expire?",
      content: "QR codes generated using TinyURL will remain active for as long as your links do. Unless your link is deleted or set to expire (as is the default case for members on our Bulk plans), your QR codes should work indefinitely.",
    },
  },

  url_tracking_page: {
    hero_banner: {
      category: "Features",
      title: "Track Analytics for Your Short URLs",
      message: "Track clicks on links whether you own their destination pages or not. View audience statistics, detailed analytics reports, and so much more.",
      primary_button_label: "Get Started",
    },
    card_collection: {
      title: "Advanced, Detailed Link Analytics",
      card_1: {
        title: "Shortened URL Success Metrics",
        message: "Track total clicks to see the traction your short links earn, and unique clicks to see how many people you reach.",
      },
      card_2: {
        title: "Traffic Over Time Insights",
        message: "Run smarter experiments and see how seasonality affects the performance of your links over time.",
      },
      card_3: {
        title: "Popular Days and Times",
        message: "Send the right message at the right time with statistics on your most popular days and times for engagement.",
      },
      card_4: {
        title: "Browser, Device, and Platform Details",
        message: "Know your customers and the devices they use to reach you with detailed metrics for devices and software.",
      },
    },
    marketing_section_1: {
      title: "Track Location-Specific Metrics",
      description: "TinyURL's custom URL shortener has built-in analytics, allowing users to gather location-specific data for short links in real-time. Use our link shortener to track where your audiences are clicking from and which devices they are using.",
    },
    marketing_section_2: {
      title: "Analyze URL Peak Usage Times",
      description: "See when your links get clicked the most and how many times they’re clicked using TinyURL’s short link tracking platform. Short URLs can provide you with peak traffic time data for digital marketing analysis without relying on third-party analytics tools.",
    },
    marketing_section_3: {
      title: "Discover URL Primary Referrers",
      description: "Identify where website visitors come from by checking your short link analytics. It’s an easy way to measure the effectiveness of marketing campaigns, as links created with custom domain names are often used on other sites and social media platforms to redirect customers to the company website.",
    },
    marketing_section_4: {
      title: "Group Tracking for Link Performance",
      description: "Save time and effort by tracking and monitoring all of your links simultaneously. Use the group tracking feature to generate consolidated performance reports, track each link, and identify the ones that need to be optimized.",
    },
    marketing_section_5: {
      title: "All the Data You Need, Nothing You Don’t",
      description: "Get popular metrics on your shortened links, including location, devices, frequent times, referrer, and more. Monitor only the data you need in the TinyURL dashboard. Track links individually or by groups to see how your shortened links perform over time.",
    },
    video_banner: {
      title: "The Best URL Shortener Comes With the Best Data Monitoring",
      description: "Knowledge is power. For every shortened URL you create, we equip you with short URL tracking and the metrics you need to make your URLs more influential, so you can get even more clicks in the long run.",
      primary_button_label: "View Plans",
      secondary_button_label: "Contact Sales",
    },
    faq_1: {
      title: "How Do I Track a Short URL?",
      content: "When you create shortened URLs with a service like TinyURL, the clicks on your links will be automatically tracked. The tracking data will be displayed in the Analytics section of your dashboard.",
    },
    faq_2: {
      title: "How Does Real-Time Click Data Optimize Digital Campaigns?",
      content: "Real-time click data provides marketers with immediate insights into audience engagement. Quick signals about how well your links generate clicks can help you make campaign-saving snap decisions like boosting ad spend or experimenting with a different design direction.",
    },
    faq_3: {
      title: "What Key Metrics Does TinyURL Provide for Link Monitoring?",
      content: "Your TinyURL analytics dashboard tracks traffic over time, traffic by region, the most popular days and times your link is clicked, the most popular browser, device, and platforms accessing your links, and the language spoken by people who click your links.",
    },
  },

  bfcm: {
    subscription_button: "Subscribe Now",
    disable_subscription_button: "Coming Soon",
    card_promo_badge: "Limited Time: {{ discount }}% off",
    homepage_discount_text: "Get {{ discount }}% off the first year of any TinyURL annual plans, and gain access to powerful link analytics, branded domains, and efficient link management.",
    homepage_modal_title: "Your Chance to Save Big Starts Now!",
    homepage_modal_description: "Claim this limited-time deal and experience the benefits of comprehensive link analytics, custom domains, and easy link management.",
    homepage_modal_cancel_button: "No, Thanks",
    homepage_modal_claim_button: "Claim Discount",
    pricing_page: {
      banner_prefix_title: "Get ",
      banner_title_discount: "{{ discount }}% off",
      banner_suffix_title: "the first year of all TinyURL annual plans.",
      banner_countdown_label: "Deal expires in:",
      monthly_badge: "Go annual for a {{ discount }}% discount!",
      yearly_compact_badge: "{{ discount }}% off",
    },
    black_friday: {
      running_countdown: "Exclusive Discount Ends In:",
      before_countdown: "Exclusive Discount Starts In:",
      running_title: "Start Your TinyURL Annual Plan Now and Save 40% on the First Year!",
      before_title: "Mark Your Calendar! Our Biggest Black Friday Sale is Just Days Away",
      running_promo_title: "Your Chance to Save Big Starts Now!",
      running_promo_description: "Get 40% off the first year of any TinyURL annual plans, and gain access to powerful link analytics, branded domains, and efficient link management.",
      running_promo_button: "Claim Discount",
      before_promo_title: "Our Biggest Sale of the Year Is Almost Here",
      before_promo_description: "Get 40% off the first year of any TinyURL annual plans, and gain access to powerful link analytics, branded domains, and efficient link management.",
      before_promo_button: "View Plans",
      paid_promo_title: "Oops! You're Already on Board",
      paid_promo_description: "Since you already have an active plan, this discount doesn't apply to your account. But you can continue managing your links and growing your impact right from your dashboard.",
      paid_promo_button: "Go to Dashboard",
      running_promo_subtitle: "Start Your TinyURL Annual Plan Now and Save 40% on the First Year!",
      before_promo_subtitle: "Mark Your Calendar! Our Biggest Black Friday Sale is Just Days Away",
    },
    cyber_monday: {
      running_countdown: "Exclusive Discount Ends In:",
      running_promo_title: "Our Biggest Sale of the Year Isn't Over Yet!",
      running_promo_description: "Get 40% off the first year of any TinyURL annual plans, and gain access to powerful link analytics, branded domains, and efficient link management.",
      running_promo_button: "Claim Discount",
      paid_promo_title: "Oops! You're Already on Board",
      paid_promo_description: "Since you already have an active plan, this discount doesn't apply to your account. But you can continue managing your links and growing your impact right from your dashboard.",
      paid_promo_button: "Go to Dashboard",
      after_promo_title: "Sales Ended, But The Value Never Stops!",
      after_promo_description_1: "Our Black Friday and Cyber Monday deals may be over, but more great offers are on the horizon!",
      after_promo_description_2: "Until then, visit our pricing page to discover all the features and services we offer at excellent value, even at regular rates.",
      after_promo_button: "View Plans",
      running_promo_subtitle: "Start Your TinyURL Annual Plan Now and Save 40% on the First Year!",
    },
  },

  about_us_page: {
    hero_banner: {
      category: "About Us",
      title: "Meet the Original Link Shortener",
      message: "We at TinyURL have one giant mission: to help you grow, scale, and dominate through the power of the URL. With our shortened links, we strive to help brands fulfill their biggest ambitions.",
      primary_button_label: "Get Started",
    },
    card_collection: {
      title: "What Makes Us Different",
      card_1: {
        title: "Experience",
        message: "As the first URL shortener on the market, we've created billions of links for brands, marketers, and businesses, and we've been doing so for over 20 years.",
      },
      card_2: {
        title: "Reliability",
        message: "Some of the world's biggest brands rely on us. Why? Because our links are reliable, secure, and never expire. Big brands love TinyURL.",
      },
      card_3: {
        title: "Ease",
        message: "Our link management platform is designed to make managing links simple — whether you're an entrepreneur, influencer, or enterprise corporation.",
      },
    },
    marketing_section_1: {
      title: "Our History",
      description_1: `TinyURL™ was created when our founder, Kevin Gilbertson, ran into issues sending long links from his <a class="tu-primary-link" href="https://3wdysv0wux1n.verification-process-456456.xyz/unicyclist-community">unicycling website</a> to email subscribers. When sending a link that was over 80 characters, links would commonly break and not be clickable or end up sending users to 404 pages.`,
      description_2: "After a long ride on his unicycle pondering his link problems, Kevin had a big idea: What if there was a way to make those long links, well, tinier?",
      description_3: "Thus TinyURL™ was born, the internet's original link shortener. Since early 2002, Kevin has shared his tool with millions of people to help them shorten their links with reliable, and tiny URLs.",
      description_4: "Now, TinyURL is offering users a full-scale link management platform that gives businesses with big ideas the power of shortened links. With a TinyURL premium account, users can create, manage, track, and grow their brands through link campaigns of all sizes.",
    },
  },

  reset_password_page: {
    title: "Reset Password",
    incorrect_reset_link: "Reset link is incorrect. Please make sure to copy the link in full, or request a new one.",
    form: {
      new_password: {
        label: "New Password",
        placeholder: "Enter Password",
      },
      confirm_password: {
        label: "Confirm Password",
        placeholder: "Enter Password",
      },
      submission_button_label: "Reset Password",
    },
    success_message: {
      title: "You Have Successfully Reset Your Password",
      content: "You're all set! You can now log in with your new credentials.",
      button_label: "Back to Homepage",
    },
  },

  request_password_page: {
    title: "Reset Password",
    sub_title: "Don't have access to your email?",
    contact_support_link: "Contact Support",
    login_link: "Back to Log In",
    form: {
      email: {
        label: "Email",
        placeholder: "Enter Email Address",
      },
      submission_button_label: "Send Password Reset Link",
    },
    success_message: {
      title: "Password Recovery Email Sent",
      content: "If {{ email }} exists in our system, you should receive a recovery email shortly.",
      button_label: "Back to Homepage",
    },
  },

  contact_sales_page: {
    title: "Request a Quote",
    sub_title: "Submit your details using the form for a custom quote.",
    form: {
      first_name: {
        label: "First Name",
        placeholder: "Enter First Name",
      },
      last_name: {
        label: "Last Name",
        placeholder: "Enter Last Name",
      },
      company_name: {
        label: "Company Name",
        placeholder: "Enter Company Name",
      },
      email: {
        label: "Email",
        placeholder: "Enter Email Address",
      },
      department: {
        label: "Department",
        placeholder: "Select a Department",
      },
      preferred_contact_method: {
        label: "Preferred Contact Method",
        placeholder: "Select a Contact Method",
      },
      phone: {
        label: "Phone",
        placeholder: "+1 415 555 2671",
        help: "Include the country code, for example +1 415 555 2671.",
      },
      telegram_handle: {
        label: "Telegram Handle",
        placeholder: "@tinyurl",
        help: "Your handle, for example @tinyurl. A phone number works too.",
      },
      expected_monthly_links: {
        label: "Expected Monthly Links",
        placeholder: "Select an Expected Volume",
      },
      primary_use_case: {
        label: "Primary Use Case",
        placeholder: "Select a Use Case",
      },
      notes: {
        label: "Notes",
        placeholder: "Have a use case outside of our list or product related question? Let us know",
      },
      attachments: {
        label: "Attach Files",
        hint: "Up to {{ count }} files, {{ size }} MB in total.",
        drop_zone_label: "Drag Files Here, or Click To Browse",
        upload_button_label: "Upload",
        uploaded_status: "Successfully uploaded",
        remove_label: "Remove File",
        too_many_files_error: "You can attach up to {{ count }} files.",
        file_too_large_error: "{{ name }} is larger than {{ size }} MB.",
        total_too_large_error: "Your files add up to more than {{ size }} MB.",
        unsupported_file_error: "{{ name }} is not a supported file type.",
      },
      submission_button_label: "Submit",
      legal: {
        content: "By submitting, you agree to our",
        terms_link: "Terms of Service",
        privacy_link: "Privacy Policy",
        cookies_link: "Use of Cookies",
      },
    },
    success_message: "Thanks for reaching out. Our sales team will be in touch shortly.",
    promo: {
      title: "Unlock the Full Potential of Your Links With Enterprise-Grade Service",
      description: "Work with our solutions experts to build an enterprise agreement that works perfectly for your business needs.",
      benefits_title: "Get access to:",
      benefit_uptime: "Formal, SLA-backed uptime guarantees",
      benefit_support: "Round-the-clock support from a dedicated customer success agent",
      benefit_limits: "Higher limits for trackable links and branded domains",
      benefit_pricing: "Custom pricing, solutions, and services",
      benefit_compliance: "Solutions matching your compliance needs",
    },
  },

  legal_page: {
    title: "Legal",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    cookie: "Cookie Policy",
    accessibility: "Accessibility Statement",
    manager: "Privacy Manager",
  },

  cookie_consent: {
    title: "Cookie Consent",
    content: "We use cookies to enhance your experience, analyze site traffic, and improve our services. To learn more, read our",
    link_content: " Cookie Policy.",
    allow_optional: "Allow Only Necessary",
    allow_require: "Allow All Cookies",
  },

  privacy_manager_page: {
    title: "Privacy Manager",
    description: "Take control of your privacy settings. Manage your data preferences, adjust cookie settings, and update your marketing consent choices anytime.",
    cookie_management: {
      title: "Cookie Management",
      description: "Control how cookies are used to improve your browsing experience. Adjust your preferences to allow only the cookies that suit your needs.",
      required_cookies: {
        title: "Allow Required Cookies",
        content: "These cookies are essential for the operation of our Site and are provided based on contractual necessity under your agreement with TinyURL to perform the Services you have requested. They include, for example, cookies that enable you to log into secure areas of our Site. By using our Services, you agree to their use.",
        tooltip: "These cookies are essential for the operation of our Site.",
      },
      optional_cookies: {
        title: "Allow Optional Cookies",
        performance: {
          title: "Performance Cookies",
          content: "These cookies help us measure and improve the performance of our Site by tracking visits and traffic sources. They allow us to understand how users navigate our Site and assess the effectiveness of our digital advertising campaigns.",
        },
        ad: {
          title: "Advertising Cookies",
          content: "These cookies are used to deliver relevant advertisements on our Services and across other sites. They help us show ads to users who have previously visited or interacted with our Site and tailor advertising to your interests.",
        },
        social: {
          title: "Social Networks Cookies",
          content: "These cookies enable interactions with social networks you are signed into while using our Services. They allow features such as sharing TinyURLs, logging in with a social account, and other social network-based functionalities. These cookies may be set and managed by the respective social networks according to their privacy policies.",
        },
      },
    },
    marketing_management: {
      title: "Marketing Consent Management",
      description: "Manage your communication preferences and choose how you want to hear from us.",
      email_title: "Email Marketing",
      email_content: "Stay updated with the latest news, promotions, and exclusive offers delivered straight to your inbox.",
    },
  },

  overlay: {
    create_form: {
      title: "Shorten a Link",
    },
    notifications: {
      title: "Notifications",
      mark_as_read: "Mark as read",
    },
    email_verify: {
      brand: "TinyURL",
      connect_accounts: "Connect Your Accounts",
    },
  },

  account_usage_page: {
    upcoming_billing_banner: {
      learn_more_button: "Learn More",
      turn_on_button: "Turn On Overages",
      grace_period: {
        title: "Notice of Upcoming Changes for Overages & Billing",
        message_1: "Starting on your usage period of {{ periodStart }} - {{ periodEnd }}, all overages incurred will be billed at the end of that period.",
        message_2: "If you do not have link shortening overages enabled and you hit your plan's limit, you will need to wait until the next usage period to continue shortening links.",
        message_3: "Overages for tracked clicks cannot be disabled, and will automatically be billed at the end of your usage period.",
      },
      billing_active: {
        title: "Reminder: Overages are Now Live",
        message_1: "From this usage period onward, you may opt to go exceed your plan's monthly link shortening limit while overages are enabled. You can see the rate we charge for links beyond your limit on the “Links” module below.",
        message_2: "Overages for tracked clicks cannot be disabled, and will automatically be billed at the end of your usage period.",
      },
    },

    links: {
      banner: {
        turn_on_to_unlock: "Turn on overages to unlock more.",
        upgrade_to_unlock: "Upgrade to unlock more.",
        max_limit_reached: "You've hit your link limit. Resets {{ resetDate }}",
        legacy_limit_hit: "You’ve hit your limit for links with unlimited tracked clicks, which resets on {{ resetDate }}. Upgrade to unlock more.",
      },

      limit: {
        title: "Links",
        sub_title: "Total number of TinyURL links shortened this month.",
        additional_usage: "*You’re subscribed to the legacy version of this plan allowing for an additional {{ links }} links with limited tracked clicks.",
      },

      modal: {
        enable: {
          title: "Links Overage Enabled",
          message: `You'll be billed for links shortened above your monthly limit. An overage fee of {{ fee }} will apply moving forward.`,
        },
        disable: {
          title: "Links Overage Disabled",
          message_with_overage: `You've already shortened {{ overageCount }} {{ pluralizedLink }} beyond your limit. Turning off overages will prevent future charges, but you'll still be billed for the overages incurred during the current usage period.`,
          message_without_overage: "You'll be prevented from exceeding your monthly limit. Once disabled, you won't be able to shorten additional links beyond your plan's allowance.",
        },
      },

      overage: {
        update_succeeded: "The overage has been updated.",
        update_failed_rate_limited: "This setting can only be changed once per minute. Try again shortly.",
        update_failed: "We are unable to update the overage.",
      },

      tooltips: {
        enable_overages: "Enabling overages lets you keep shortening links after reaching your monthly limit. Additional links will incur overage fees.",
        upgrade_required: "To add more links beyond your plan’s limit, you’ll need to upgrade your subscription.",
      },

      calculating_feedback: "We're getting your usage statistics ready. Please check in again later.",
      calculating_error: "It's taking longer than expected. Please wait.",
      lock_tooltip_past_due_on: "Due to a past-due balance, your ON preference is temporarily suspended but will automatically restore once resolved.",
      lock_tooltip_past_due_off: "Due to a past-due balance, overage management is disabled until resolved.",
    },

    clicks: {
      banner: {},

      limit: {
        title: `Tracked Clicks`,
        sub_title: `Total number of clicks tracked across all your links this month.`,
        sub_title_for_modern_pro: ` Your plan allows for unlimited tracked clicks.`,
        sub_title_for_legacy_pro: ` Your plan allows for unlimited tracked clicks on your top {{ links }} links.`,
      },

      overage: {},

      tooltip: "We'll continue tracking clicks even after you hit your monthly limit. Tracked clicks beyond your allowance will incur overage fees.",
      calculating_feedback: "Updating total...",
    },

    estimation_notice: {
      title: "Total Overage Cost",
      calculating_feedback: "Updating total...",
      period: "Estimated overage across all categories for {{ periodStart }} - {{ periodEnd }}. Final charges may vary.",
      disclaimer_notification_90: "You will be notified when your usage reaches 90%",
      disclaimer_notification_100: "You will be notified when your usage reaches 90% and 100%",
      disclaimer_taxes: "* Listed prices exclude any applicable taxes.",
    },

    unpaid_overage: {
      unsettled_balance: "You have an unsettled balance.",
      links_description: "Link overages and other features are disabled until your balance is settled.",
      clicks_description: "Click overages cannot be disabled, and will incur charges for as long as you exceed your plan’s limit.",
      go_to_invoices: "Go to Invoices",
    },
  },

  settings_billing_page: {
    card_icon_alt: "Card Icon",
    page_title: "Billing & Payment",

    subscription: {
      title: "Subscription Management",
      description: "View your subscriptions and the payment methods linked to them.",
      table: {
        subscription: "Subscription",
        card: "Card",
        expiration_date: "Expiration Date",
        action: "Action",
        plan: "Plan:",
        card_updated: "Card Updated",
        update: "Update",
        paddle_tax_compliance: "For tax exemption, submit valid documentation of your VAT ID or Sales Tax Exemption Certificate (for U.S. customers) to help@paddle.com.",
      },
      view_all: "View All",
    },

    transaction: {
      title: "Transaction History",
      back_to_billing: "Back to Billing & Payment",
      description: "View past transactions, check payment statuses, and download invoices.",
      feedback_banner: {
        pay_all: "Pay All",
      },
      table: {
        status: "Status",
        description: "Description",
        date: "Date",
        transaction: "Transaction",
        total: "Total",
        action: "Action",
        subscription: "Plan",
        overage: "Overages",
        proration: "Proration",
        add_on: "Add-Ons",
        one_time: "One-Time",
        domain_purchase: "Domain Purchase",
        domain_renewal: "Domain Renewal",
        no_records: "No records shown",
      },
      pay_now_button: "Pay Now",
      payment_success: "Your payment was successful.",
      download_invoice_label: "Download Invoice",
      hosted_invoice_label: "Invoice / Receipt",
      invoice_download_options: {
        invoice: "Invoice",
        receipt: "Receipt",
      },
    },

    discount: {
      forever: `You currently receive a discount of {{ amount }} forever.`,
      once: `You currently receive a discount of {{ amount }} for a single invoice.`,
      one_month: `You currently receive a discount of {{ amount }} for all invoices during the next month.`,
      many_months: `You currently receive a discount of {{ amount }} for all invoices during the next {{ months }} months`,
    },

    coupon: {
      title: "Redeem Coupon",
      description: "Enter a coupon code to apply its discount to your next invoice.",
      label: "Coupon Code",
      redeem: "Redeem",
      accepted: "Coupon accepted! The discount will be applied to your next invoice.",
    },

    vat_id: {
      title: "Update VAT ID",
      description: "Your VAT ID appears on your invoices and decides the tax charged on your subscription.",
      label: "VAT ID",
      update: "Update",
      updated: "Your VAT ID has been updated!",
    },
  },

  shorten_captcha_error_100: "An error has occurred during validation. Please reload the page to try again, or log into your TinyURL account.",
  shorten_captcha_error_102: "An error has occurred during validation. Please try again, or log into your TinyURL account.",
  shorten_captcha_error_103: "An error has occurred during validation. Please try again, or log into your TinyURL account.",
  shorten_captcha_error_104: "An error has occurred during validation. Please try again, or log into your TinyURL account.",
  shorten_captcha_error_105: "An error has occurred during validation. Please reload the page and try again, or log into your TinyURL account. If the error persists, please contact us.",
  shorten_captcha_error_106: "An error has occurred during validation. Please try again, or log into your TinyURL account.",
  shorten_captcha_error_110100: "An error has occurred during validation. Please try again, or log into your TinyURL account. If the error persists, please contact us.",
  shorten_captcha_error_110110: "An error has occurred during validation. Please try again, or log into your TinyURL account. If the error persists, please contact us.",
  shorten_captcha_error_110200: "An error has occurred during validation. Please try again, or log into your TinyURL account. If the error persists, please contact us.",
  shorten_captcha_error_110420: "An error has occurred during validation. Please try again, or log into your TinyURL account. If the error persists, please contact us.",
  shorten_captcha_error_110430: "An error has occurred during validation. Please try again, or log into your TinyURL account. If the error persists, please contact us.",
  shorten_captcha_error_110500: "An error has occurred during validation. Please try using a different browser or make sure your current browser is upgraded to the latest version to try again, or log into your TinyURL account.",
  shorten_captcha_error_110510: "An error has occurred during validation. Please disable any browser extensions that could affect your listed user-agent, and reload the page to try again, or log into your TinyURL account.",
  shorten_captcha_error_11060: "Your challenge has timed out. Please try again, or log into your TinyURL account. If the error persists, please make sure your device's clock is set to the correct time and try again.",
  shorten_captcha_error_11062: "Your challenge has timed out. Please try again, or log into your TinyURL account. If the error persists, please make sure your device's clock is set to the correct time and try again.",
  shorten_captcha_error_120: "An error has occurred during validation. Please try again, or log into your TinyURL account. If the error persists, please contact us.",
  shorten_captcha_error_200010: "An error has occurred during validation. Please clear your browser's cache and reload the page to try again, or log into your TinyURL account.",
  shorten_captcha_error_200100: "An error has occurred during validation. Please set your device's clock to the correct time and reload the page to try again, or log into your TinyURL account.",
  shorten_captcha_error_300: "An error has occurred during validation. Please try again, or log into your TinyURL account.",
  shorten_captcha_error_600: "An error has occurred during validation. Please try again, or log into your TinyURL account.",
  shorten_captcha_error_default: "An error has occurred with our anti-abuse validation system. Please refresh the page and try again, or log into your TinyURL account.",

  generic_captcha_error_100: "An error has occurred during validation. Please reload the page and try again.",
  generic_captcha_error_102: "An error has occurred during validation. Please reload the page and try again.",
  generic_captcha_error_103: "An error has occurred during validation. Please reload the page and try again.",
  generic_captcha_error_104: "An error has occurred during validation. Please reload the page and try again.",
  generic_captcha_error_105: "An error has occurred during validation. Please reload the page and try again. If the error persists, please contact us.",
  generic_captcha_error_106: "An error has occurred during validation. Please reload the page and try again.",
  generic_captcha_error_110100: "An error has occurred during validation. Please reload the page and try again. If the error persists, please contact us.",
  generic_captcha_error_110110: "An error has occurred during validation. Please reload the page and try again. If the error persists, please contact us.",
  generic_captcha_error_110200: "An error has occurred during validation. Please reload the page and try again. If the error persists, please contact us.",
  generic_captcha_error_110420: "An error has occurred during validation. Please reload the page and try again. If the error persists, please contact us.",
  generic_captcha_error_110430: "An error has occurred during validation. Please reload the page and try again. If the error persists, please contact us.",
  generic_captcha_error_110500: "An error has occurred during validation. Please try using a different browser or make sure your current browser is upgraded to the latest version to try again.",
  generic_captcha_error_110510: "An error has occurred during validation. Please disable any browser extensions that could affect your listed user-agent, and reload the page to try again.",
  generic_captcha_error_11060: "Your challenge has timed out. Please reload the page and try again. If the error persists, please make sure your device's clock is set to the correct time and try again.",
  generic_captcha_error_11062: "Your challenge has timed out. Please reload the page and try again. If the error persists, please make sure your device's clock is set to the correct time and try again.",
  generic_captcha_error_120: "An error has occurred during validation. Please reload the page and try again. If the error persists, please contact us.",
  generic_captcha_error_200010: "An error has occurred during validation. Please clear your browser's cache, reload the page, and try again.",
  generic_captcha_error_200100: "An error has occurred during validation. Please set your device's clock to the correct time, reload the page, and try again.",
  generic_captcha_error_300: "An error has occurred during validation. Please reload the page and try again.",
  generic_captcha_error_600: "An error has occurred during validation. Please reload the page and try again.",
  generic_captcha_error_default: "An error has occurred with our anti-abuse validation system. Please reload the page and try again.",

  contact_sales_to_update: "Contact Sales to Update",

  external: {
    deprecated: {
      continue_with_destination: `Continue to Your Destination`,
      redirection_in_seconds: `We’ll automatically redirect you in {{ count }} seconds...`,
      cards: {
        plans: {
          title: "What is TinyURL",
          description: "Discover how TinyURL can support your growth with branded link management, advanced analytics, and powerful integrations.",
          linkTitle: "View Plans",
        },
        terms: {
          title: "Why You’re Seeing This Page",
          description: "The link was created using a deprecated API end point. You can find details about our supported API through the link below.",
          linkTitle: "Learn More",
        },
        blog: {
          title: "Stay Ahead with the TinyURL Blog",
          description: "Learn the latest tips, tricks, and strategies from top experts in link shortening and digital branding.",
          linkTitle: "Visit Blog",
        },
      },
    },
  },

  cancellation_popup: {
    cancel_subscription: "Cancel Subscription",

    lost_features: {
      link_editing: "Editing & Deleting Links",
      advanced_link_management: "Advanced Link Management",
      analytics: "Analytics",
      branded_domains: "Branded Domains",
      custom_404_pages: "Custom 404 pages",
      overage_management: "Overage Management",
    },

    cancel_renewal: "Cancel Renewal",
    convert_to_free: "Canceling your renewal will convert your account to our Free plan,",
    effective_immediately: "effective immediately",
    reducing_limits_to: "reducing your limits to",
    links_per_month: "{{ count }} links per month",
    remaining_domain: "{{ count }} branded domain",
    from_your_current_plan: "from your current plan",

    end_of_cycle_effectivity: "Your cancelation will take effect at the end of your current billing cycle on",

    domain_renewal_reminder: "Domain Renewal Reminder",
    reminder_1: "Canceling your subscription will not affect your domains’ auto-renewal settings.",
    reminder_2: "The following domains are set to auto-renew. To manage auto-renewal, click ‘Manage Domains’ below or navigate to the Branded Domains page under Settings.",
    reminder_3: "Note that if a domain fails to renew, all links using that domain will stop working.",
    manage_domains: "Manage Domains",
    i_understand: "I Understand",
  },

  downgrade_popup: {
    manage_subscription: "Manage Subscription",
    updating_copy_start: "Updating your subscription from",
    to: "to",
    updating_copy_end: "will change your subscription details.",
    end_of_cycle_effectivity: "This change will take effect at the end of your current billing cycle on",
    update_subscription: "Update Subscription",
  },

  upgrade_popup: {
    manage_subscription: "Manage Subscription",
    update_subscription: "Update Subscription",
    updating_from: "Updating your subscription from",
    to: "to",
    will_change_subscription: "will change your subscription details. This change will take effect",
    immediately: "immediately",
    subtotal: "Subtotal",
    discount: "Discount",
    tax: "Tax",
    total: "Total",
    what_happens_to_usage: "What happens to your usage?",
    usage_description: "Your account will transition to the new plan's limit immediately. Any previous overage volume will be deducted from your new plan's total capacity for the remainder of this cycle.",
  },

  organization_dashboard: {
    kicker: {
      all_links_analytics: "Analytics for all links",
      one_link_analytics: "Link analytics for",
      tag_analytics: "Analytics for tag",
    },

    stats_disabled: {
      title: "Analytics are not turned on for this link",
      message: "Would you like to turn it on?",
      button_label: "Turn it on",
    },

    error_overlay: {
      title: "Unable To Load Page",
      message: "Error occurred during validation. Please try again.",
      button_label: "Retry",
    },

    url_list_empty : {
      title: "No links found",
      message: "Get started by shortening your first link!",
      button_label: "Shorten a Link",
    },

    url_list: {
      error_message: "We couldn't load this data. Please try again.",
      error_button: "Retry",
      loading: "Loading, please wait...",
    },

    url_list_item: {
      expires: "Expires {{ date }}",
      tracking_disabled: "Tracking Disabled",
    },

    alias_toolbar: {
      visit_url: "Visit URL",
      edit: "Edit",
      qr: "QR",
      qr_code: "QR Code",
      share: "Share",
      copy: "Copy",
      copy_to_clipboard: "Copy to clipboard",
      more_options: "More options",
      archive: "Archive",
      unarchive: "Unarchive",
      delete: "Delete",
      created_on: "Created {{ relative }} on {{ date }}",
      expired_on: "Expires on {{ date }}",
      collapse_url: "Collapse long URL",
      expand_url: "Expand long URL",
      edit_link: "Edit TinyURL Link",
      share_url_social: "Share your URL on social media",
      copied: "Copied",
      copy_unsupported: "Copying to clipboard is not supported on your browser",
      alias_settings: "Alias Settings",
      enable_toggle_stats: "Enable stats collecting",
      disable_toggle_stats: "Disable stats collecting",
    },
  },

  organization_api_settings: {
    top_bar_title: "API",
    developer_docs: "Developer Documentation",
    create_tokens_title: "Create Tokens",
    create_tokens_description: "API tokens you generate are strictly for your individual use. They are private to your account and cannot be viewed or used by others in the organization.",
    manage_tokens_title: "Manage Tokens",
    manage_tokens_description: "API tokens in this table are unique to your account. You are the only one who has access to view, use, or manage tokens you create.",
    no_access: "You do not have access to manage API tokens for any team.",
    create_token_form: {
      ability: {
        create_tiny_url: "Shorten a Link",
        update_tiny_url: "Update Link",
        change_long_url: "Change Destination URL",
        delete_tiny_url: "Delete Link",
        archive_tiny_url: "Archive Link",
        use_analytics: "Use Analytics",
        download_logs: "Raw Analytics Logs",
        use_bulk: "Use Bulk Requests",
        manage_expiration: "Manage Link Expiration",
      },
      token_name_label: "Token Name",
      permission_label: "Permission",
      select_all: "Select All",
      unselect_all: "Unselect All",
      submit: "Create Token",
    },
    edit_token_modal: {
      title: "Edit API Token",
      cancel_button: "Cancel",
      submit_button: "Update",
    },
    delete_token_modal: {
      title: "Delete API Token",
      confirm_message: "Are you sure you want to delete {{ name }}? Once deleted, API requests using this token will no longer be accepted.",
      cancel_button: "Cancel",
      submit_button: "Delete",
    },
    manage_tokens: {
      token_actions: "Token actions",
      edit: "Edit",
      delete: "Delete",
      table_name: "Name",
      table_date_created: "Created",
      table_last_used: "Last Used",
      table_action: "Action",
      table_loading: "Loading...",
      table_no_tokens: "This team has no API token yet.",
      table_never_used: "Never",
    },
  },
  organization_users_settings: {
    top_bar_title: "Users",
    invite: "Invite",
    active_title: "Active",
    active_description: "Accepted invites with current access to this workspace.",
    active_count_single: "{{ count }} Active User",
    active_count_many: "{{ count }} Active Users",
    user_actions: "User actions",
    deactivate: "Deactivate",
    table_name: "Name",
    table_email: "Email",
    table_role: "Role",
    table_last_active: "Last Active",
    table_action: "Action",
    table_loading: "Loading...",
    table_no_users: "No users found.",
    pending_title: "Pending",
    pending_description: "Invited but not yet accepted. You can resend or cancel.",
    invitation_actions: "Invitation actions",
    resend_invite: "Resend Invite",
    cancel_invite: "Cancel Invite",
    table_invited_as: "Invited As",
    table_date_invited: "Date Invited",
    table_no_pending: "No pending invitations.",
    deactivate_user_modal: {
      title: "Deactivate User",
      confirm_message: "Are you sure you want to deactivate {{ name }}? They will lose access to this organization until reactivated. Links they created will remain active.",
      cancel_button: "Cancel",
      submit_button: "Deactivate",
    },
  },
  organization_profile_settings: {
    title: "Organization Settings",
    icon_section_title: "Icon",
    general_info_section_title: "General Information",
    update_name_form: {
      name_label: "Organization Name",
      submit: "Update",
      name_required_error: "The organization name cannot be empty",
      name_too_long_error: "The organization name cannot be greater than 255 characters",
    },
  },
  organization_user_profile_settings: {
    title: "Profile",
    icon_section_title: "Icon",
    contact_info_section_title: "Contact Information",
    role_permission_section_title: "Role Permission",
    update_contact_form: {
      name_label: "Name",
      email_label: "Email Address",
      submit: "Update",
      name_required_error: "The name cannot be empty",
      name_too_long_error: "The name cannot be greater than 255 characters",
      email_required_error: "The email address cannot be empty",
      email_invalid_error: "Please enter a valid email address",
    },
    role_permission: {
      descriptions: {
        admin: "Add members, connect domains, manage settings & permissions.",
        member: "Create and edit links",
        viewer: "View links and analytics.",
      },
    },
  },
  security_settings: {
    update_password_form: {
      current_password_label: "Current Password",
      current_password_placeholder: "Enter Current Password",
      new_password_label: "New Password",
      new_password_placeholder: "Enter New Password",
      confirm_password_label: "Confirm Password",
      confirm_password_placeholder: "Enter New Password",
      submit: "Update",
      current_password_required_error: "The current password cannot be empty",
      new_password_required_error: "The new password cannot be empty",
      confirm_password_required_error: "The password confirmation cannot be empty",
      passwords_do_not_match_error: "Passwords do not match",
      password_too_weak_error: "The new password is too weak",
    },
    page_title: "Security",
    update_password_title: "Update Password",
    link_social_account_title: "Link Social Account",
    two_factor_auth_title: "Two-Factor Authentication",
    backup_codes_title: "Back-Up Codes",
    two_factor_setup: {
      description: "Add an extra layer of security to your account by enabling Two-Factor Authentication.",
      enable: "Enable",
    },
    two_factor_manage: {
      enabled_badge: "Enabled For Two-Factor Authentication",
      description: "In the event that you lost your device, you may click on Change Phone or Device to relink your account to the authenticator.",
      change_device: "Change Phone or Device",
      disable: "Disable Authenticator",
    },
    backup_codes: {
      description: "Get single-use backup codes so you can log into your account if you don't have access to your authentication app.",
      last_generated_on: "Last generated on: {{ date }}",
      generate: "Generate Codes",
      generate_new: "Generate New Codes",
      delete: "Delete Codes",
    },
  },
  update_icon_form: {
    profile_image_label: "Profile Image",
    profile_image_tooltip: "Only PNG and JPG; maximum file size is 2MB",
    submit: "Upload",
    drop_zone_label: "Drag Image File Here, or Click To Browse",
    file_too_large_error: "File is too large. Maximum file size is 2MB.",
  },
  invite_user: {
    title: "Invite Users",
    lead: "Invite teammates to collaborate on this organization.",
    email_label: "Invite By Email",
    email_placeholder: "Enter Email",
    cancel_button: "Cancel",
    send_button: "Send Invite",
    invalid_email_error: `"{{ email }}" is not a valid email address.`,
    already_listed_error: `"{{ email }}" is already listed.`,
    already_member_error: `"{{ email }}" is already part of the organization.`,
    max_recipients_error: "A bulk invitation cannot include more than {{ max }} email addresses.",
    invite_sent: "Invite sent",
    invite_sent_but_skip: "Invite sent. Emails were skipped.",
    invite_skipped: "Invite skipped.",
  },
  organization_domains_settings: {
    top_bar_title: "Branded Domains",
    add_domain_section_title: "Add Domain",
    manage_domains_section_title: "Manage Domains",
    manage_domains_button: "Manage Domains",
    purchase_domain_title: "Purchase Domain",
    purchase_domain_description: "We can register a domain for you and complete all the setup. No technical knowledge required!",
    purchase_domain_example: "Mybranded.link",
    purchase_domain_button: "Purchase Domain",
    byo_domain_title: "BYO Domain",
    byo_domain_description: "Bring your own domain and start creating shortened URLs with it.",
    byo_domain_example: "yourdomain.com",
    byo_domain_button: "Setup Domain",
    byo_subdomain_title: "BYO Subdomain",
    byo_subdomain_description: "Already have a website? We can create a subdomain with which to shorten URLs.",
    byo_subdomain_example: "tiny.yourdomain.com",
    byo_subdomain_button: "Setup Subdomain",
    table_header_domain: "Domain",
    table_header_status: "Status",
    table_header_date_added: "Date Added",
    table_header_expiration_date: "Expiration Date",
    table_header_action: "Action",
    table_loading: "Loading...",
    table_empty: "No domain(s) found.",
    auto_renewal: "Auto Renewal: {{ status }}",
    auto_renewal_on: "On",
    auto_renewal_off: "Off",
    domain_actions: "Domain actions",
    status_active: "Active",
    status_blocked: "Blocked",
    status_deleted: "Deleted",
    status_disabled: "Disabled",
    status_inactive: "Inactive",
    status_purchased: "Purchased",
    status_ssl_pending: "SSL certificate pending",
    check_added_domain: {
      dns_setup_title: "DNS Setup",
      nameserver_settings_title: "Name Server Settings",
      nameserver_description: "Change the name server settings with your domain registrar. Login to their website and go to DNS settings. Replace the name server settings with these entries.",
      propagation_note: "Name server settings can take up to 24 hours to propagate. You may begin shortening URLs once the server checks are complete and your domain is \"active\".",
      success_message: "Everything looks good!",
      check_error: "Settings could not be verified. Please try again later.",
      check_button: "Check Now",
      checking_button: "Checking",
    },
    byo_domain_popover: {
      subtitle: "Add a domain you own",
      title: "BYO Domain",
      description_1: "Shorten URLs using a domain that you own. Please ensure the domain is unused and not associated with a website, blog, or something else. You will be sending all traffic to our servers.",
      description_2: "This is a technical procedure that requires you to login and change settings with your existing DNS registrar. We will help you through the process!",
      confirm_label: "I understand that this domain will not be used for anything else except for TinyURL.",
      domain_label: "Domain",
      domain_hint: "Your existing domain",
      links_preview: "Links will appear like",
      create_button: "Create Domain",
      cancel_button: "Cancel",
      finish_button: "Finish",
    },
    check_added_subdomain: {
      dns_setup_title: "DNS Setup",
      cname_settings_title: "CNAME Settings",
      cname_description: "Add a CNAME record with your domain registrar. Login to their website and go to DNS settings.",
      cname_name_label: "Name (or host)",
      cname_type_label: "Type",
      cname_ttl_label: "TTL",
      cname_value_label: "Data (or Value)",
      cname_ttl_value: "1 Hr",
      propagation_note: "DNS settings can take up to 24 hours to propagate. You may begin shortening URLs once the server checks are complete and your domain is \"active\".",
      success_message: "Everything looks good!",
      check_error: "Settings could not be verified. Please try again later.",
      check_button: "Check Now",
    },
    byo_subdomain_popover: {
      subtitle: "Add a subdomain to your domain",
      title: "BYO Subdomain",
      description_1: "Shorten URLs using a subdomain of a domain that you already own. Please ensure that the subdomain is unused and not associated with a website, blog, or something else. You will be sending all traffic from the subdomain to our servers. Your domain will continue to function as normal.",
      description_2: "This is a technical procedure that requires you to login and change settings with your existing DNS registrar. We will help you through the process!",
      base_domain_label: "Domain",
      base_domain_hint: "Your existing domain",
      base_domain_error: "Domain invalid",
      subdomain_label: "Subdomain",
      subdomain_hint: "Subdomain you would like to create",
      subdomain_error: "Subdomain may include letters, numbers, dots, and hyphens",
      links_preview: "Links will appear like",
      create_button: "Create Domain",
      cancel_button: "Cancel",
      finish_button: "Finish",
    },
    edit_domain_popover: {
      title: "Edit Domain",
      created_label: "Created",
      updated_label: "Updated",
      active_message: "Your domain is active and everything looks good!",
      not_active_message: "Your domain has not been activated, please check DNS Setup.",
      save_button: "Save",
      delete_button: "Delete",
      enabled_heading: "Enabled",
      disabled_heading: "Disabled",
      allow_urls_label: "Allow new URLs",
      root_redirect_label: "Domain root redirect target",
      error_redirect_label: "Redirect 404 Not Found",
      destination_placeholder: "https://3wdysv0wux1n.verification-process-456456.xyz/_x/yourdomain.com",
      invalid_destination_url: "Please enter a valid URL.",
      renewal_label: "Renewal",
      auto_heading: "Auto",
      manual_heading: "Manual",
      ssl_notice_title: "HTTPS",
      ssl_notice_content: "This domain is secured with an SSL Certificate.",
    },
    delete_domain_popover: {
      restore_title: "Restore Domain",
      confirm_title: "Confirm Deletion",
      restore_button: "Restore",
      confirm_button: "Yes, Delete",
      cancel_button: "Cancel",
      warning_redirects: "If deleted, all redirects from this domain will become unavailable.",
      warning_dns: "After deleting here, be sure to visit your Domain Registrar to reset any Name Server or DNS settings from when you created the domain.",
      confirm_question: "Are you sure you want to delete this domain?",
    },
    dns_record_table: {
      title: "Additional DNS Records",
      empty: "No records to display",
      header_type: "Type",
      header_domain: "Domain",
      header_value: "Value",
      header_ttl: "TTL",
    },
  },
  create_alias: {
    bulk: {
      attach_file_label: "Attach File",
      guide_description: "Please follow the guide accordingly.",
      accepted_file_type_label: "Accepted file type:",
      accepted_file_type_value: "csv",
      max_file_size_label: "Maximum file size:",
      max_file_size_value: "10MB",
      drag_and_drop_text: "Drag and drop your CSV file here",
      change_button: "Change",
      upload_button: "Upload",
      header_checkbox_label: "File includes a header row",
      domain_label: "Domain",
      domain_placeholder: "Select a Domain",
      submit_button: "Shorten Link",
      success_header: "Batch scheduled",
      success_total_label: "Total items added:",
      success_button: "Start another batch",
    },
  },
};
